/**
 * PDP album configurator — the Preact island that renders the buy-box.
 *
 * Extracted verbatim from the inline `<script type="module">` that used to live
 * in sections/product.liquid (lines 675-2086). It moved out of the document for
 * one reason: inline JS is re-downloaded and re-compiled on every page view and
 * never enters the browser's bytecode cache. As an external module it is cached
 * like any other asset, so a repeat PDP visit pays nothing for it.
 *
 * The three theme asset URLs this module needs (`pdp-vendor.js`,
 * `delivery-deadline-banner.js`, `icon-gift.svg`) arrive through the
 * `#pdp-config` JSON island as `cfg.assets`, because an external module has no
 * `document.currentScript` to read data attributes from.
 *
 * TWO SURFACES, ONE MODULE. The PDP auto-mounts on import, exactly as before:
 * if the document carries a `#pdp-config` island the module configures itself
 * from it and renders into `#pdp-configurator-root`. The cart drawer's edit
 * panel mounts the SAME module against a config fetched from the product's PDP
 * (see `assets/cart-edit-host.js`), which is why nothing below may read the
 * island at import time: on a collection page there is none.
 *
 * So `cfg` and everything derived from it are per-`configure()` state rather
 * than module constants, and the Preact runtime is imported lazily by `mount()`
 * — the asset URLs live inside the config that configure() has yet to receive.
 */

import { resolveInstallments } from './pdp-installments.js';
import { sanitizeText } from './pdp-engraving-allowlist.js';
import { PREVIEW_POS, PREVIEW_FS, PREVIEW_GROUP_BY_SLUG, PREVIEW_FS_FALLBACK } from './pdp-preview-geometry.js';
import { createSpotifySearch } from './pdp-spotify-search.js';
import { trackLabel } from './spotify-track-label.js';
import { applyOrigin, originsOfGroup } from './addon-origin.js';
import { sellableVariants, cheapestVariant } from './pdp-sellable-variants.js';

// Per-configure state. `cfg` is the parsed `#pdp-config` island of ONE product;
// configure() rebinds it together with the twelve values derived from it.
let cfg = null;

// A/B switch (single-theme model): both trees live in THIS module.
// 'A' = the current step wizard below; 'B' = the single-screen
// "Personalize seu álbum" tree merged in from the old product-b.liquid.
// The shared parts (buildProperties, _group/_personalizacao, ?edit=,
// #pdp-config, gallery) stay ONE codebase — only the view tree branches.
// TODO(merge): port product-b's single-screen views as `AppB` and
// return it when abVariant === 'B'. See docs/DESIGN-ab-onetheme.md.
// Everything below reads the page through these two, which are null under
// `node --test`. That is what lets the concurrency in mount()/unmount() be
// driven by a test (test/pdp-configurator-mount.test.js) instead of only
// grepped for: importing the module outside a browser has to be inert, not a
// ReferenceError. In the browser they are the real globals, unchanged.
const pageWindow = typeof window === 'undefined' ? null : window;
const pageDocument = typeof document === 'undefined' ? null : document;

const abVariant = (pageWindow && pageWindow.fnExperiment && pageWindow.fnExperiment.variant) === 'B' ? 'B' : 'A';

// Preact runtime + the two optional side modules, resolved once per document by
// loadRuntime(). They cannot be top-level awaits any more: their URLs live in
// `cfg.assets`, and on a non-PDP page cfg only arrives when the drawer's edit
// panel opens.
let createElement;
let render;
let useState;
let useEffect;
let useRef;
let html;
let ddbModule = null;
let captureModule = null;
let runtimePromise = null;

async function loadRuntime(assets) {
  if (runtimePromise) return runtimePromise;
  runtimePromise = (async () => {
    const [vendor, ddb, capture] = await Promise.all([
      import(assets.vendor),
      import(assets.deliveryDeadlineBanner),
      // Guarded twice: a cached page can carry an island older than this module,
      // and a failed fetch must never gate the buy box's mount (fail-open).
      assets.previewCapture ? import(assets.previewCapture).catch(() => null) : Promise.resolve(null),
    ]);
    ({ h: createElement, render, useState, useEffect, useRef } = vendor);
    html = vendor.htm.bind(createElement);
    ddbModule = ddb;
    captureModule = capture;
  })();
  return runtimePromise;
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const money = (cents) => BRL.format((cents || 0) / 100);
let ddbGiftIconUrl = '';
let strings = {};
let iconMap = {};
let iconBoldMap = {};
let iconLabelMap = {};

// ── Spotify track search ─────────────────────────────────────
// The client lives in pdp-spotify-search.js, shared with the beta album
// configurator — one token cache, one retry, one result shape, so the two
// arms cannot drift. Created lazily because `cfg` only lands in configure().
let spotifySearchFor = null;
let spotifySearchClient = null;
const spotifySearch = (query, limit) => {
  if (!spotifySearchClient || spotifySearchFor !== cfg) {
    spotifySearchFor = cfg;
    spotifySearchClient = createSpotifySearch(cfg.spotify || {});
  }
  return spotifySearchClient(query, limit);
};

// Shared store so the gallery preview (a separate render root) reacts to
// the configurator's color/text/foil state without a framework-level bridge.
const store = {
  state: { mode: 'main', colorIndex: 0, foilId: 'prata', coverText: '', sideText: '', titleSize: 'M', sideSize: 'P', sideIcon: '' },
  subs: new Set(),
  set(patch) {
    this.state = Object.assign({}, this.state, patch);
    this.subs.forEach((fn) => fn(this.state));
  },
};
function useStore() {
  const [s, setS] = useState(store.state);
  useEffect(() => {
    store.subs.add(setS);
    setS(store.state);
    return () => store.subs.delete(setS);
  }, []);
  return s;
}

const variantById = (id) => cfg.variants.find((v) => v.id === id) || cfg.variants[0];
let baseVariant = null;
let hasQtyAxis = false;

// Per-product / per-size character limits, ported from the React
// textSizingUtils (getTextLimit / getSideTextLimit). Cover scales by
// titleSize P/M/G, spine by sideSize P/M; limits are per line.
const TEXT_LIMITS = {
  'mini-pocket': { p: 11, m: 11, g: 11, sideP: 0, sideM: 0 },
  'classic-pocket': { p: 14, m: 11, g: 14, sideP: 26, sideM: 26 },
  'big-pocket': { p: 23, m: 17, g: 23, sideP: 30, sideM: 30 },
  'large-pocket': { p: 33, m: 22, g: 17, sideP: 42, sideM: 29 },
  'table-book-grande': { p: 46, m: 32, g: 24, sideP: 42, sideM: 30 },
  'table-book-midi': { p: 46, m: 32, g: 24, sideP: 35, sideM: 24 },
  'scrapbook-grande': { p: 46, m: 32, g: 24, sideP: 42, sideM: 30 },
  'scrapbook-midi': { p: 46, m: 32, g: 24, sideP: 35, sideM: 24 },
  'scrapbook-short': { p: 23, m: 17, g: 23, sideP: 30, sideM: 30 },
  'scrap-book-a3': { p: 46, m: 32, g: 24, sideP: 42, sideM: 30 },
  'scrap-book-a4': { p: 46, m: 32, g: 24, sideP: 35, sideM: 24 },
  'scrap-book-a5': { p: 23, m: 17, g: 23, sideP: 30, sideM: 30 },
};
// Selected photo count → printed-page range. The custom.faixas_de_paginas metafield
// takes precedence; this per-handle map is the legacy fallback, ported 1:1 from the
// web app's getPageQuantityMapping.
// Value is the numeric range only; strings.pagesSuffix ("páginas") is appended at render.
// Scrapbooks are intentionally absent (no page counter defined), so the indicator
// simply hides.
const PAGE_RANGES = {
  'mini-pocket': { 20: '20', 24: '24', 28: '28' },
  'classic-pocket': { 20: '24 a 26', 35: '38 a 40', 50: '60 a 64' },
  'big-pocket': { 20: '24 a 26', 35: '38 a 40', 50: '60 a 64', 70: '70 a 76' },
  'large-pocket': { 20: '24 a 26', 35: '38 a 40', 50: '60 a 64', 70: '70 a 76', 90: '80 a 84', 110: '100' },
  'table-book-a3': { 70: '44 a 48', 90: '56 a 60', 150: '70 a 76', 200: '96 a 100' },
  'table-book-a4': { 70: '44 a 48', 90: '56 a 60', 150: '70 a 76', 200: '96 a 100' },
  'table-book-grande': { 70: '44 a 48', 90: '56 a 60', 150: '70 a 76', 200: '96 a 100' },
  'table-book-midi': { 70: '44 a 48', 90: '56 a 60', 150: '70 a 76', 200: '96 a 100' },
};
// Title/spine sizes + per-size limits derive from char_limits (metafield);
// fall back to the legacy TEXT_LIMITS map when the metafield is absent.
const SIZE_ORDER = ['P', 'M', 'G'];
let CL_COVER = null;
let CL_SPINE = null;
let titleSizes = ['P', 'M', 'G'];
let sideSizes = ['P', 'M'];
let spineMode = 2; // 0 = no spine, 1 = fixed P, 2 = P/M selector
let maxLines = 3;
let hasMusic = false;

const coverLimit = (size) => {
  const sizeKey = (size || 'P').toUpperCase();
  if (CL_COVER && CL_COVER[sizeKey] != null) return CL_COVER[sizeKey];
  const t = TEXT_LIMITS[cfg.product.slug];
  return t ? (t[sizeKey.toLowerCase()] || t.p) : 20;
};
const sideLimit = (size) => {
  const sizeKey = (size || 'P').toUpperCase();
  if (CL_SPINE && CL_SPINE[sizeKey] != null) return CL_SPINE[sizeKey];
  const t = TEXT_LIMITS[cfg.product.slug];
  return t ? (sizeKey === 'M' ? t.sideM : t.sideP) : 20;
};

let previewGeo = PREVIEW_POS.classic;
let previewFs = {};
const coverFontFor = (size) => previewFs['cover' + (size || 'P').toUpperCase()] || previewFs.coverM || previewFs.coverP || 4;
const spineFontFor = (size) => previewFs['spine' + (size || 'P').toUpperCase()] || previewFs.spineP || previewFs.spineM || 3;

// The inert facade every disabled path degrades to: the endpoint is blank
// outside the allowed stores, the module may be absent on a stale island, and
// nothing may gate add-to-cart. The cart line then falls back to `_cover_image`.
const INERT_CAPTURE = { schedule() {}, clear() {}, current: () => null, settle: async () => null };
let previewCapture = INERT_CAPTURE;
let detachCapture = null;

// Arm the capture for the config currently in `cfg`. Called by mount(), after
// the runtime is in — createPreviewCapture lives in the lazily imported module.
// Detaching first matters: the store is module scope, so the PDP's subscriber
// would otherwise keep scheduling captures against its own product while the
// drawer's edit panel drives a different one.
function armCapture() {
  if (detachCapture) {
    detachCapture();
    detachCapture = null;
  }
  previewCapture = captureModule
    ? captureModule.createPreviewCapture({
        endpoint: (cfg.routes && cfg.routes.previewUpload) || '',
        geo: previewGeo,
        iconMap,
        iconBoldMap,
      })
    : INERT_CAPTURE;
  if (!captureModule) return;
  detachCapture = captureModule.attachToStore({
    store,
    capture: previewCapture,
    // Flow A arms only at the recap step — the 'later' summary on the main
    // view never captures. The drawer's edit panel is a flat screen with no
    // steps, so it arms for as long as it is mounted: every field on it is a
    // field the recap would have captured.
    shouldCapture: (storeState) => storeState.mode === 3 || storeState.panel === true,
    colors: cfg.colors,
    colorImages: cfg.colorImages,
    previewBase: cfg.product && cfg.product.previewBase,
    coverFontFor,
    spineFontFor,
  });
}

/**
 * Bind this module to one product's `#pdp-config` payload. Everything the views
 * read out of `cfg` is recomputed here, so a second call fully re-targets the
 * module — that is how the drawer's edit panel borrows it for a product other
 * than the one the page is showing.
 */
export function configure(rawConfig) {
  cfg = { ...rawConfig, variants: sellableVariants(rawConfig.variants) };
  strings = cfg.strings || {};
  ddbGiftIconUrl = cfg.assets.giftIcon;
  iconMap = {};
  iconBoldMap = {};
  iconLabelMap = {};
  (cfg.icons || []).forEach((ic) => {
    iconMap[ic.name] = ic.url;
    iconBoldMap[ic.name] = ic.boldUrl || ic.url;
    iconLabelMap[ic.name] = ic.label || ic.name;
  });
  baseVariant = cheapestVariant(cfg.variants) || { price: cfg.product.basePrice };
  hasQtyAxis = cfg.variants.length > 1;
  CL_COVER = (cfg.charLimits && cfg.charLimits.cover) || null;
  CL_SPINE = (cfg.charLimits && cfg.charLimits.spine) || null;
  titleSizes = CL_COVER ? SIZE_ORDER.filter((sizeKey) => CL_COVER[sizeKey] != null) : ['P', 'M', 'G'];
  sideSizes = CL_SPINE ? SIZE_ORDER.filter((sizeKey) => CL_SPINE[sizeKey] != null) : ['P', 'M'];
  spineMode = sideSizes.length;
  maxLines = Number(cfg.maxLines) || 3;
  hasMusic = cfg.hasMusic != null ? cfg.hasMusic : !/book/.test(cfg.product.slug || '');
  const previewGroup = PREVIEW_GROUP_BY_SLUG[cfg.product.slug] || (cfg.isScrapbook ? 'landscape' : 'classic');
  previewGeo = PREVIEW_POS[previewGroup] || PREVIEW_POS.classic;
  previewFs = PREVIEW_FS[cfg.product.slug] || PREVIEW_FS[PREVIEW_FS_FALLBACK[previewGroup]] || {};
}

// Perceptual luminance → pick a dark check on light swatches, white on dark ones.
const isLightHex = (hex) => {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return true;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
};

const previewSymbol = (name) =>
  html`<span class="pdp-preview__ico" style=${`--ico:url(${iconMap[name]});--ico-bold:url(${iconBoldMap[name]})`} aria-hidden="true"><span class="pdp-preview__ico-glyph"></span></span>`;

function Money({ cents, className }) {
  return html`<span class=${className}>${money(cents)}</span>`;
}

function PriceBlock({ variant }) {
  const price = variant.price;
  const compare = variant.compareAt;
  const hasCompare = compare && compare > price;
  const pct = hasCompare ? Math.round((1 - price / compare) * 100) : 0;
  const { count: installmentCount, perInstallmentCents: installment } = resolveInstallments(price, cfg.installmentsRule);
  return html`
    <div class="pdp-price">
      <div class="pdp-price__row">
        <${Money} className="pdp-price__now" cents=${price} />
        ${hasCompare && html`<${Money} className="pdp-price__was" cents=${compare} />`}
        ${hasCompare && html`<span class="pdp-price__off">-${pct}%</span>`}
      </div>
      <p class="pdp-price__installment">
        ${strings.installmentsPrefix} ${installmentCount}x de ${money(installment)} sem juros
      </p>
      <p class="pdp-price__pix">${money(price * (1 - (Number(cfg.pixPercent) || 0) / 100))} ${strings.pixSuffix}</p>
    </div>
  `;
}

function PromoGiftBadge() {
  const [badge, setBadge] = useState(null);
  useEffect(() => {
    if (!cfg.assets.giftBadge || !cfg.routes.liveGiftBadge) return undefined;
    let active = true;
    import(cfg.assets.giftBadge)
      .then((module) => module.fetchGiftBadge(cfg.routes.liveGiftBadge))
      .then((found) => { if (active) setBadge(found); })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  if (!badge) return null;
  return html`
    <div class="pdp-gift-badge">
      ${badge.image && html`<img class="pdp-badge__img" src=${badge.image} alt="" width="56" height="56" loading="lazy" />`}
      <div class="pdp-badge__text">
        ${badge.eyebrow && html`<p class="pdp-badge__eyebrow">${badge.eyebrow}</p>`}
        <p class="pdp-badge__title">${badge.title}</p>
      </div>
    </div>
  `;
}

function QtyPills({ selectedId, onSelect }) {
  if (!hasQtyAxis) return null;
  const selectedPhotos = parseInt(variantById(selectedId).title, 10);
  const rangeMap = cfg.pageRanges || PAGE_RANGES[cfg.product.slug];
  const range = rangeMap && rangeMap[selectedPhotos];
  const pages = range ? range + ' ' + strings.pagesSuffix : '';
  return html`
    <div class="pdp-field">
      <div class="pdp-field__labelrow">
        <p class="pdp-field__label">${strings.qtyLabel}</p>
        ${strings.tipQty && html`<${Tooltip} text=${strings.tipQty} />`}
        ${pages && html`<span class="pdp-field__pages">${pages}</span>`}
      </div>
      <div class="pdp-pills" role="radiogroup" aria-label=${strings.qtyLabel}>
        ${cfg.variants.map((v) => {
          const delta = v.price - baseVariant.price;
          const isSel = v.id === selectedId;
          return html`
            <button
              type="button"
              key=${v.id}
              class=${'pdp-pill' + (isSel ? ' is-selected' : '') + (v.available ? '' : ' is-disabled')}
              role="radio"
              aria-checked=${isSel}
              disabled=${!v.available}
              onClick=${() => onSelect(v.id)}
            >
              <span class="pdp-pill__main">${v.title.replace(/\s*fotos?$/i, '')}</span>
              <span class="pdp-pill__sub">${delta <= 0 ? strings.included : '+' + money(delta)}</span>
            </button>
          `;
        })}
      </div>
      ${strings.qtyHelper && html`<button type="button" class="pdp-field__help" onClick=${openPopup('fotos')}>${strings.qtyHelper}</button>`}
    </div>
  `;
}

function SwatchGrid({ items, selected, onSelect, label, readout, inlineReadout, checkSelect, tip }) {
  const scrollRef = useRef(null);
  const thumbRef = useRef(null);
  // Custom always-visible scroll indicator for the horizontal swatch row (mobile).
  const updateThumb = () => {
    const el = scrollRef.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;
    const scrollable = el.scrollWidth > el.clientWidth;
    const ratio = scrollable ? el.clientWidth / el.scrollWidth : 1;
    thumb.style.width = ratio * 100 + '%';
    thumb.style.left = (scrollable ? (el.scrollLeft / el.scrollWidth) * 100 : 0) + '%';
  };
  useEffect(() => {
    updateThumb();
  }, []);
  return html`
    <div class=${'pdp-field' + (checkSelect ? ' pdp-field--swatch' : '')}>
      <div class="pdp-field__labelrow">
        <p class="pdp-field__label">${label}</p>
        ${tip && html`<${Tooltip} text=${tip} />`}
        ${inlineReadout && selected && html`<span class="pdp-field__readout">${selected.label}</span>`}
      </div>
      <div class=${'pdp-swatches' + (checkSelect ? ' pdp-swatches--check' : '')} ref=${scrollRef} onScroll=${updateThumb} role="radiogroup" aria-label=${label}>
        ${items.map((c) => {
          const isSel = selected && selected.label === c.label;
          return html`
            <button
              type="button"
              key=${c.label}
              class=${'pdp-swatch' + (isSel ? ' is-selected' : '')}
              role="radio"
              aria-checked=${isSel}
              aria-label=${c.label}
              title=${c.label}
              onClick=${() => onSelect(c)}
            >
              <span class="pdp-swatch__chip" style=${'--swatch: ' + c.hex + "; --swatch-img: " + (c.texture ? "url('" + c.texture + "')" : 'none')}></span>
              ${checkSelect && isSel && html`<svg class="pdp-swatch__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" style=${'color: ' + (isLightHex(c.hex) ? '#151616' : '#ffffff')}><path d="M20 6 9 17l-5-5"></path></svg>`}
            </button>
          `;
        })}
      </div>
      ${checkSelect && html`<div class="pdp-swatches-scroll" aria-hidden="true"><span class="pdp-swatches-scroll__thumb" ref=${thumbRef}></span></div>`}
      ${readout && selected && html`
        <div class="pdp-readout">
          <span class="pdp-readout__chip" style=${'--swatch: ' + selected.hex + "; --swatch-img: " + (selected.texture ? "url('" + selected.texture + "')" : 'none')}></span>
          <span class="pdp-readout__name">${selected.label}</span>
          <svg class="pdp-readout__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>
        </div>
      `}
    </div>
  `;
}

function SegPills({ label, readout, options, value, onSelect }) {
  return html`
    <div class="pdp-field pdp-field--seg">
      <div class="pdp-field__labelrow">
        <p class="pdp-field__label pdp-field__label--sm">${label}</p>
        ${readout && html`<span class="pdp-field__readout pdp-field__readout--light">${readout}</span>`}
      </div>
      <div class="pdp-seg" role="radiogroup" aria-label=${label}>
        ${options.map((o) => html`
          <button
            type="button"
            key=${o}
            class=${'pdp-seg__item' + (value === o ? ' is-selected' : '')}
            role="radio"
            aria-checked=${value === o}
            onClick=${() => onSelect(o)}
          >${o}</button>
        `)}
      </div>
    </div>
  `;
}

function StepHeader({ step, title }) {
  return html`
    <div class="pdp-step__head">
      <div class="pdp-step__row">
        <h2 class="pdp-step__title">${title}</h2>
        <span class="pdp-step__badge">${strings.stepOf.replace('{n}', step)}</span>
      </div>
      <span class="pdp-step__rule"></span>
    </div>
  `;
}

// Built per render rather than once at module scope: `html` is bound by
// loadRuntime(), which cannot have run by the time this file is evaluated.
// htm memoises by template-literal identity, so this costs a map lookup.
const tipIcon = () => html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9.5"></circle><path d="M12 11v5"></path><path d="M12 7.6h.01"></path></svg>`;

// Dark tooltip on an ⓘ icon — hover on desktop, tap on mobile. The trigger
// is a role="button" span (not a <button>) so it stays valid HTML when it
// sits inside another button, e.g. a protection tile. align="end" right-aligns
// the bubble so it never clips off-screen near the right edge.
function Tooltip({ text, align }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);
  if (!text) return null;
  const toggle = (e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); };
  return html`
    <span class=${'pdp-tip' + (open ? ' is-open' : '')} ref=${ref}
      onMouseEnter=${() => setOpen(true)} onMouseLeave=${() => setOpen(false)}>
      <span class="pdp-tip__btn" role="button" tabindex="0" aria-label=${text} aria-expanded=${open}
        onClick=${toggle}
        onKeyDown=${(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(e); }}>
        ${tipIcon()}
      </span>
      <span class=${'pdp-tip__bubble' + (align ? ' pdp-tip__bubble--' + align : '')} role="tooltip">${text}</span>
    </span>
  `;
}

// Opens one of the "Ver mais" popups rendered as native <dialog> in the
// Liquid template (outside the Preact root). Returns an onClick handler.
const openPopup = (id) => (e) => {
  if (e) e.preventDefault();
  const dlg = document.getElementById('pdp-popup-' + id);
  if (!dlg) return;
  if (typeof dlg.showModal === 'function') dlg.showModal();
  else dlg.setAttribute('open', '');
};

/**
 * The buy box.
 *
 * `panel` switches it from the PDP's three-step wizard to the flat, single
 * screen the cart drawer's edit panel shows: same state, same price maths, same
 * cart contract — one scrolling column of fields instead of steps, with SAVE
 * driven from the drawer's own footer through `panelRoot`.
 *
 * `initialEditKey` is the cart line to hydrate from. The PDP passes nothing and
 * reads `?edit=` off the URL instead; the panel already knows the key.
 */
const GIFT_MARKS = ['_fn_brinde', 'Brinde', '_group', '_fn_brinde_preco', '_fn_brinde_slot'];
const ALBUM_CHOICES = [
  'Cor da capa', '_cover_image', '_cover_hex', '_preview_image', 'Cor da gravação', 'Texto da capa', 'Tamanho do título',
  'Símbolos da capa', 'Texto da lateral', 'Tamanho da lateral', 'Símbolo da lateral', '_personalizacao',
];
const GIFT_ADDONS = ['caixa_luva', 'caixa_cristal', 'musica', 'linha'];

const lineProps = (item) => (item && item.properties) || {};
const isGiftLine = (item) => !!lineProps(item)._fn_brinde;
const withoutOrigin = (props) => Object.fromEntries(Object.entries(props || {}).filter(([name]) => name !== '_addon_origin'));
const sameEntries = (left, right) => Array.from(new Set([...Object.keys(left), ...Object.keys(right)]))
  .every((name) => (left[name] || '') === (right[name] || ''));
const isLineFor = (line, item) => Number(line.variant_id) === Number(item.id) && line.quantity === item.quantity
  && sameEntries(withoutOrigin(lineProps(line)), withoutOrigin(item.properties));
const sameKind = (line, item) => Number(line.variant_id) === Number(item.id) && lineProps(line)._addon === lineProps(item)._addon;

function readGiftGroup(cart, group) {
  const inGroup = ((cart && cart.items) || []).filter((item) => group && lineProps(item)._group === group);
  return {
    album: inGroup.find((item) => isGiftLine(item) && !lineProps(item)._addon) || null,
    paid: inGroup.filter((item) => !isGiftLine(item) && GIFT_ADDONS.includes(lineProps(item)._addon)),
  };
}

function giftAlbumProps(current, chosen) {
  const kept = Object.fromEntries(Object.entries(current).filter(([name]) => !ALBUM_CHOICES.includes(name)));
  const marks = Object.fromEntries(GIFT_MARKS.filter((name) => name in current).map((name) => [name, current[name]]));
  return { ...kept, ...chosen, ...marks };
}

function rewrittenAddon(line, item) {
  const properties = { ...item.properties };
  const origin = lineProps(line)._addon_origin;
  if (origin) properties._addon_origin = origin;
  else delete properties._addon_origin;
  return properties;
}

function holdsExactly(bag, wanted, albumProps) {
  if (!bag.album || !sameEntries(lineProps(bag.album), albumProps) || bag.album.quantity !== 1) return false;
  const left = bag.paid.slice();
  const found = wanted.every((item) => {
    const index = left.findIndex((line) => isLineFor(line, item));
    if (index === -1) return false;
    left.splice(index, 1);
    return true;
  });
  return found && !left.length;
}

const jsonRequest = (url, body) => fetch(url, body
  ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
  : { headers: { Accept: 'application/json' } })
  .then((response) => response.json().then((data) => ({ ok: response.ok, data })));

const pageEnqueue = (task) => {
  const cart = pageWindow && pageWindow.fnCart;
  return cart && typeof cart.enqueueWrite === 'function' ? cart.enqueueWrite(task) : task();
};

export class GiftSaveError extends Error {
  constructor(message, sections) {
    super(message);
    this.name = 'GiftSaveError';
    this.sections = sections || null;
  }
}

export function saveAlbumGift({
  routes, group, chosen, addons, messages = {}, sectionsUrl = '', request = jsonRequest, enqueue = pageEnqueue,
}) {
  let sections = null;
  let wrote = false;
  const fail = (reason) => {
    throw new GiftSaveError(reason || messages.addError || '', sections);
  };
  const read = async () => {
    const cart = await request(routes.cart).catch(() => null);
    if (!(cart && cart.ok && cart.data && Array.isArray(cart.data.items))) fail();
    return readGiftGroup(cart.data, group);
  };
  const post = async (url, body) => {
    const response = await request(url, { ...body, sections: 'cart-drawer', sections_url: sectionsUrl }).catch(() => null);
    if (!(response && response.ok)) fail(response && response.data && (response.data.description || response.data.message));
    wrote = true;
    sections = (response.data && response.data.sections) || sections;
  };

  return enqueue(async () => {
    if (addons.some((item) => !GIFT_ADDONS.includes(lineProps(item)._addon) || lineProps(item)._group !== group)) fail();
    const before = await read();
    if (!before.album) fail(messages.lineGone);

    const unclaimed = before.paid.slice();
    const claim = (matches) => {
      const index = unclaimed.findIndex(matches);
      return index === -1 ? null : unclaimed.splice(index, 1)[0];
    };
    const kept = addons.map((item) => claim((line) => isLineFor(line, item)));
    const rewrites = [];
    const missing = [];
    addons.forEach((item, index) => {
      if (kept[index]) return;
      const line = claim((candidate) => sameKind(candidate, item));
      if (line) rewrites.push({ line, item });
      else missing.push(item);
    });

    for (const { line, item } of rewrites) {
      await post(routes.cartChange, { id: line.key, quantity: item.quantity, properties: rewrittenAddon(line, item) });
    }
    if (unclaimed.length) await post(routes.cartUpdate, { updates: Object.fromEntries(unclaimed.map((line) => [line.key, 0])) });
    if (missing.length) await post(routes.cartAdd, { items: missing });

    const current = lineProps(before.album);
    const albumProps = giftAlbumProps(current, chosen);
    if (before.album.quantity !== 1 || !sameEntries(albumProps, current)) {
      await post(routes.cartChange, { id: before.album.key, quantity: 1, properties: albumProps });
    }
    if (wrote && !holdsExactly(await read(), addons, albumProps)) fail();
    return sections;
  });
}

function App({ panel, panelRoot, initialEditKey }) {
  const [variantId, setVariantId] = useState(variantById(cfg.product.selectedVariantId).id);
  const [mode, setMode] = useState('main'); // 'main' | step number
  const [color, setColor] = useState(cfg.colors[0] || null);
  const [foil, setFoil] = useState(cfg.foils.find((foilOpt) => foilOpt.id === 'prata') || cfg.foils[0]);
  const [titleSize, setTitleSize] = useState(titleSizes.includes('P') ? 'P' : titleSizes[0] || 'P');
  const [sideSize, setSideSize] = useState(sideSizes.includes('P') ? 'P' : sideSizes[0] || 'P');
  const [coverLines, setCoverLines] = useState(['']);
  const [coverIcons, setCoverIcons] = useState(['']);
  const [iconPicker, setIconPicker] = useState(-1);
  const [sideText, setSideText] = useState('');
  const [sideIcon, setSideIcon] = useState('');
  const [sidePicker, setSidePicker] = useState(false);
  const coverText = coverLines.join('\n');
  const extraLines = coverLines.length - 1;
  const [protection, setProtection] = useState('none');
  const [spotify, setSpotify] = useState('');            // search box text
  const [spotifyTrack, setSpotifyTrack] = useState(null); // locked-in track | null
  const [spotifyResults, setSpotifyResults] = useState([]);
  const [spotifyLoading, setSpotifyLoading] = useState(false);
  const [spotifyError, setSpotifyError] = useState(false);
  const [spotifySearched, setSpotifySearched] = useState(false); // a query has resolved → allow empty state
  const searchSeq = useRef(0); // monotonic token: a superseded Spotify response is ignored
  const [choice, setChoice] = useState('depois');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  // Edit mode (cart drawer linked here with ?edit=<line_key>): set after the
  // line is located in /cart.js. editGroupKeys holds the old group's line keys
  // (album + add-ons) to zero on save. A ref: it must not trigger re-renders.
  const [editKey, setEditKey] = useState(null);
  const editGroupKeys = useRef([]);
  // The old group's id. It is what survives an add: Shopify re-keys the bag when
  // a line lands, so a key read before the add can name a DIFFERENT line after
  // it. `_group` is ours and stays put, so it is what re-finds the old lines at
  // removal time (see saveEdit).
  const editGroupId = useRef(null);
  const editAddonOrigins = useRef({});
  const [giftEdit, setGiftEdit] = useState(null);
  // What this screen's save has done so far: the group id it is trying to create
  // (reused by every retry, so a lost add response cannot become two
  // replacements), the keys to drop, and whether the replacement is already in.
  // A ref: it must not trigger re-renders.
  const editSaveAttempt = useRef(null);
  // Whether a save is in flight RIGHT NOW. `adding` cannot answer that: it is
  // state, so a second call reads whatever the current render closed over, and
  // `setAdding(true)` only lands on the next render. Two clicks inside that
  // window both got past the guard and raced — one outcome left the add-on of
  // the losing group behind (invisible in the drawer, still charged), the other
  // removed the group that had just become the live one and reported success
  // with the album gone from the bag. A ref flips synchronously, before the
  // first await, so the window never opens.
  const editSaving = useRef(false);
  const newGroupId = () => (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : ('g' + Date.now() + Math.round(Math.random() * 1e6));
  const PA = cfg.photoAddon;
  const hasPhotoAddon = Boolean(PA && PA.sizes && PA.sizes.length);
  const [photoTab, setPhotoTab] = useState('auto');
  const variantIdFor = (size, tabId) => {
    return size.variantIds && size.variantIds[tabId];
  };
  const sizesForTab = (tabId) => {
    return PA.sizes.filter((size) => variantIdFor(size, tabId));
  };
  const [photoQtys, setPhotoQtys] = useState({});
  // Typing the quantity. photoQtyDrafts holds the raw keystrokes per size so the
  // minimum is enforced on blur rather than per keystroke — clamping while typing
  // would turn the "1" of "100" into the minimum and swallow the rest. Neither
  // handler may commit null or NaN: setSizeQty(id, null) removes the size.
  const [photoQtyDrafts, setPhotoQtyDrafts] = useState({});
  // Anything that commits without passing through the field drops the draft with
  // it — WebKit doesn't blur a focused field on button tap, so a surviving draft
  // keeps rendering over the committed value and a later blur recommits it.
  const clearSizeDraft = (id) => {
    setPhotoQtyDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  const setSizeQty = (id, quantity) => {
    setPhotoQtys((prev) => ({ ...prev, [id]: quantity }));
    // A size that leaves the order takes its draft along; reopening it later must
    // start from the fresh quantity, not from what was typed before it left.
    if (quantity == null) clearSizeDraft(id);
  };
  const selectPhotoTab = (tabId) => {
    setPhotoTab(tabId);
    PA.sizes.forEach((size) => {
      if (!variantIdFor(size, tabId)) setSizeQty(size.id, null);
    });
  };
  // The size toggle. Closing goes through setSizeQty(null), which drops the draft
  // on its own; only the opening path commits a number and has to drop it here.
  const openSizeQty = (id, isOpen) => {
    if (isOpen) {
      setSizeQty(id, null);
      return;
    }
    clearSizeDraft(id);
    const size = PA.sizes.find((entry) => entry.id === id);
    const suggested = size && size.suggestion && size.suggestion[photoTab];
    setSizeQty(id, clampQty(suggested || PA.featuredQty, minFor(size)));
  };
  // The field's maxlength caps what can be TYPED into it; this caps what blur and
  // the stepper commit, and the stepper never passes through the field at all.
  // The 9999 fallback is the same ceiling maxlength="4" already imposes, so a
  // config island written before maxQty existed cannot turn Math.min into NaN.
  // `|| PA.minQty` runs first on purpose: it absorbs NaN and 0 before either Math
  // sees one — Math.min(ceiling, NaN) is NaN, and NaN reaches the order as null.
  // The minimum is applied LAST and wins: it is a per-size product rule, the
  // ceiling an operational setting — a maxQty below some size's minimum must
  // not push a committed quantity under the rule buildProperties ships.
  // Floored because max_qty accepts decimals in the editor, and a 100.5 ceiling
  // winning the Math.min would post a fractional quantity the cart rejects.
  const clampQty = (quantity, minQty = PA.minQty) => {
    return Math.max(minQty, Math.floor(Math.min(9999, Number(PA.maxQty) || 9999, quantity || minQty)));
  };
  const minFor = (size) => {
    return (size && Number(size.minQty)) || PA.minQty;
  };
  const onQtyInput = (id, event) => {
    const digits = event.currentTarget.value.replace(/\D/g, '');
    setPhotoQtyDrafts((prev) => ({ ...prev, [id]: digits }));
    const typed = parseInt(digits, 10);
    if (typed > 0) setSizeQty(id, typed);
  };
  const onQtyBlur = (id, event) => {
    const typed = parseInt(event.currentTarget.value, 10);
    clearSizeDraft(id);
    const size = PA.sizes.find((entry) => entry.id === id);
    const clamped = clampQty(typed, minFor(size));
    setSizeQty(id, clamped);
    // Announce only when the clamp CHANGED the value — an unchanged number was
    // already echoed by the screen reader while typing.
    if (clamped !== typed) {
      setQtyStatus(`${PA.qtyLabel}${size ? ' — ' + size.label : ''}: ${clamped}`);
    }
  };
  const onQtyKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };
  // The step buttons read state through the functional updater, never the render
  // closure: blur and click land in the same task, so the closure's q is still
  // the pre-clamp value — a bare q + 1 once committed 6 against a minimum of 20,
  // and after emptying the field it revived the stale pre-edit quantity.
  // Announced through the hidden role="status" element: writes to an input's
  // .value are not DOM mutations, so aria-live on the field itself never fires.
  // Only the buttons announce — the screen reader already echoes typing.
  const [qtyStatus, setQtyStatus] = useState('');
  const stepSizeQty = (id, delta) => {
    // The draft is what the field renders, so it — not the committed quantity —
    // is the base the step must start from whenever it exists. WebKit doesn't
    // blur a focused field on button tap, so without this an emptied field steps
    // from the stale pre-edit quantity (80 → 81 instead of 21).
    // Read through an updater, never from the render closure: a keystroke and a
    // click landing in the same task leave the closure a render behind, and the
    // step would read the draft from before the last key. Returning `prev`
    // unchanged is a bailout — no extra render.
    let draft;
    setPhotoQtyDrafts((prev) => {
      draft = prev[id];
      return prev;
    });
    let next;
    const size = PA.sizes.find((entry) => entry.id === id);
    setPhotoQtys((prev) => {
      // Clamp the base before stepping it: a raw typed 5 has to become the
      // minimum first, or + commits 6 and the button reads as dead.
      const base = draft === undefined ? prev[id] : parseInt(draft, 10);
      next = clampQty(clampQty(base, minFor(size)) + delta, minFor(size));
      return { ...prev, [id]: next };
    });
    clearSizeDraft(id);
    setQtyStatus(`${PA.qtyLabel}${size ? ' — ' + size.label : ''}: ${next}`);
  };
  const photoTierFor = (qty) => {
    if (!PA) return null;
    let best = null;
    PA.tiers.forEach((tier) => { if (qty >= tier.q && (!best || tier.q > best.q)) best = tier; });
    return best;
  };
  const photoTotal = () => {
    if (!PA) return 0;
    return PA.sizes.reduce((sum, s) => {
      const q = photoQtys[s.id];
      if (q == null) return sum;
      const per = s.prices[photoTab] || 0;
      const t = photoTierFor(q);
      return sum + Math.round(per * (1 - (t ? t.p : 0) / 100)) * q;
    }, 0);
  };

  // The estimator belongs to the PDP, not to the drawer's edit panel — and on a
  // PDP page the panel is showing a DIFFERENT product, so retargeting the host's
  // island from here would quote the wrong variant.
  useEffect(() => {
    if (panel) return;
    const host = document.getElementById('pdp-prazo-host');
    const island = host && host.querySelector('[data-prazo-config]');
    if (!island) return;
    const current = variantById(variantId);
    try {
      const prazoCfg = JSON.parse(island.textContent);
      prazoCfg.variant = { id: current.id };
      island.textContent = JSON.stringify(prazoCfg);
    } catch { /* leave the config as-is */ }
    const prazoPanel = host.querySelector('[data-prazo-panel]');
    if (prazoPanel) prazoPanel.hidden = true;
  }, [variantId]);

  const quoteLinesRef = useRef(null);
  quoteLinesRef.current = () => buildCartItems('', 'quote', { commit: false });
  useEffect(() => {
    if (panel) return undefined;
    const host = document.getElementById('pdp-prazo-host');
    if (!host) return undefined;
    const answer = (event) => {
      if (!host.contains(event.target)) return;
      event.detail.lines = quoteLinesRef.current();
    };
    document.addEventListener('prazo:request-lines', answer);
    return () => document.removeEventListener('prazo:request-lines', answer);
  }, []);

  // Mirror the personalization state into the shared store for the gallery preview.
  useEffect(() => {
    store.set({
      mode: typeof mode === 'number' ? mode : 'main',
      panel: !!panel,
      colorIndex: color ? cfg.colors.findIndex((c) => c.label === color.label) : 0,
      foilId: foil ? foil.id : 'prata',
      coverText: coverText,
      coverIcons: coverIcons,
      sideText: sideText,
      titleSize: titleSize,
      sideSize: sideSize,
      sideIcon: sideIcon,
    });
    // Everything below reshapes the PDP around the wizard. The edit panel is not
    // on the PDP — it renders inside the cart drawer, over whatever page the
    // buyer is on — so it must leave that page's DOM alone.
    if (panel) return;
    // Hide the gallery thumbnails while the wizard preview is showing (any step).
    const sectionEl = document.querySelector('.pdp');
    const wizardActive = typeof mode === 'number';
    if (sectionEl) sectionEl.setAttribute('data-wizard', wizardActive ? 'true' : 'false');

    // Wizard focus mode: collapse the marketing sections that sit between the PDP
    // and the footer so the customer stays focused on personalization. These render
    // as sibling `.shopify-section` wrappers in content_for_layout; the footer
    // (`.site-footer`, last sibling) and everything above the PDP are preserved.
    // Sections reappear on return to the main view.
    const productWrapper = sectionEl && sectionEl.closest('.shopify-section');
    if (productWrapper) {
      let sibling = productWrapper.nextElementSibling;
      while (sibling) {
        if (sibling.classList.contains('shopify-section') && !sibling.querySelector('.site-footer')) {
          sibling.classList.toggle('pdp-focus-hidden', wizardActive);
        }
        sibling = sibling.nextElementSibling;
      }
    }
  }, [mode, color, foil, coverText, coverIcons, sideText, titleSize, sideSize, sideIcon]);

  // Announce the wizard step for the analytics listeners in the <head>, which
  // turn it into a Clarity custom tag. One effect on `mode` rather than a call
  // at each of the six `setMode` sites, so a step reached by any route — a Next
  // button, the back button, edit hydration — reports itself exactly once.
  //
  // Only numbered steps are announced: 'main' is the buy-box, not a step of the
  // wizard. And not from the drawer's edit panel, which hydrates straight to
  // step 1 without the buyer ever having walked the wizard.
  useEffect(() => {
    if (panel || typeof mode !== 'number') return;
    document.dispatchEvent(new CustomEvent('fn:pdp-step', { detail: { step: mode } }));
  }, [mode]);

  // Edit mode hydration: load the cart line being edited, restore the wizard
  // state from its _personalizacao blob + same-group add-ons, capture the group
  // keys to remove on save, and jump to Passo 1. If the key is gone (cart
  // changed), stay on the main view — graceful, no error.
  //
  // The key comes from `?edit=<line_key>` on the PDP; the drawer's edit panel
  // already holds it and passes it in, because it never navigates.
  useEffect(() => {
    const key = initialEditKey || new URLSearchParams(window.location.search).get('edit');
    if (!key) return;
    let cancelled = false;
    fetch(cfg.routes.cart, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((cart) => {
        if (cancelled) return;
        const items = cart.items || [];
        const line = items.find((it) => it.key === key);
        if (!line) return;
        const props = line.properties || {};
        const group = props._group;
        editGroupKeys.current = group ? items.filter((it) => (it.properties || {})._group === group).map((it) => it.key) : [line.key];
        editGroupId.current = group || null;
        setVariantId(variantById(line.variant_id).id);
        let blob = null;
        try { blob = props._personalizacao ? JSON.parse(props._personalizacao) : null; } catch { blob = null; }
        if (blob) {
          if (blob.cor) {
            const matched = cfg.colors.find((colorOpt) => colorOpt.label === blob.cor.label)
              || cfg.colors.find((colorOpt) => colorOpt.hex === blob.cor.hex)
              || cfg.colors[0] || null;
            setColor(matched);
          }
          if (blob.gravacao) {
            const matchedFoil = cfg.foils.find((foilOpt) => foilOpt.id === blob.gravacao);
            if (matchedFoil) setFoil(matchedFoil);
          }
          if (blob.titulo) {
            if (blob.titulo.tamanho) setTitleSize(blob.titulo.tamanho);
            const titleLines = Array.isArray(blob.titulo.linhas) ? blob.titulo.linhas : [];
            if (titleLines.length) {
              setCoverLines(titleLines.map((titleLine) => titleLine.texto || ''));
              setCoverIcons(titleLines.map((titleLine) => titleLine.simbolo || ''));
            }
          }
          if (blob.lateral) {
            setSideText(blob.lateral.texto || '');
            if (blob.lateral.tamanho) setSideSize(blob.lateral.tamanho);
            setSideIcon(blob.lateral.simbolo || '');
          }
        }
        editAddonOrigins.current = originsOfGroup(items.filter((it) => (it.properties || {})._group === group));
        if (isGiftLine(line) && group) {
          editAddonOrigins.current = { box: 'cart', musica: 'cart', ...editAddonOrigins.current };
          setGiftEdit({ group });
        }
        items.forEach((it) => {
          const addonProps = it.properties || {};
          if (addonProps._group !== group || !addonProps._addon) return;
          if (addonProps._addon === 'caixa_luva') setProtection('luva');
          else if (addonProps._addon === 'caixa_cristal') setProtection('cristal');
          else if (addonProps._addon === 'musica') {
            const songLabel = addonProps['Música'] || '';
            const songParts = songLabel.split(' — ');
            setSpotifyTrack({ id: '', name: songParts[0] || songLabel, artist: songParts[1] || '', image: addonProps._spotify_image || '', url: addonProps._spotify_url || '' });
          }
        });
        setChoice('agora');
        // Edit mode skips the PDP's own deadline estimator (design: the deadline
        // block is not part of the edit flow). It is server-rendered outside the
        // Preact root, so hide it imperatively once edit mode is confirmed — but
        // only on the PDP; from the drawer that host belongs to another product.
        if (!panel) {
          const prazoHost = document.getElementById('pdp-prazo-host');
          if (prazoHost) prazoHost.hidden = true;
        }
        setEditKey(key);
        setMode(1);
      })
      .catch(() => { /* graceful fallback: stay on main view */ });
    return () => { cancelled = true; };
  }, []);

  // Shared Spotify search runner. Each call claims the next searchSeq token;
  // a response only lands while its token is still current, so a slow request
  // can never overwrite a newer one — whether fired by the debounce or the
  // button. Resetting state (effect cleanup below) also bumps the token.
  const runSearch = (q) => {
    const seq = (searchSeq.current += 1);
    setSpotifyLoading(true);
    setSpotifyError(false);
    spotifySearch(q, 5)
      .then((results) => { if (seq === searchSeq.current) { setSpotifyResults(results); setSpotifySearched(true); } })
      .catch(() => { if (seq === searchSeq.current) { setSpotifyResults([]); setSpotifyError(true); } })
      .finally(() => { if (seq === searchSeq.current) setSpotifyLoading(false); });
  };

  // Type-ahead Spotify search: fire 500ms after typing stops, once the box
  // has ≥2 chars. A locked-in track suppresses the list (the chosen card is
  // shown instead); editing the box clears the lock via onSpotifyInput. The
  // cleanup bumps searchSeq so a pending response is dropped when deps change.
  useEffect(() => {
    if (!hasMusic) return undefined;
    const q = spotify.trim();
    if (spotifyTrack || q.length < 2) {
      setSpotifyResults([]);
      setSpotifyError(false);
      setSpotifySearched(false);
      setSpotifyLoading(false);
      return undefined;
    }
    setSpotifyLoading(true);
    setSpotifyError(false);
    const timer = setTimeout(() => runSearch(q), 500);
    return () => { clearTimeout(timer); searchSeq.current += 1; };
  }, [spotify, spotifyTrack, hasMusic]);

  // Editing the query drops any locked-in track so the list can show again.
  const onSpotifyInput = (value) => {
    setSpotify(value);
    if (spotifyTrack) setSpotifyTrack(null);
  };
  const pickTrack = (track) => {
    setSpotifyTrack(track);
    setSpotifyResults([]);
    setSpotifySearched(false);
    setSpotifyError(false);
  };
  const clearTrack = () => {
    setSpotifyTrack(null);
    setSpotify('');
    setSpotifyResults([]);
    setSpotifySearched(false);
    setSpotifyError(false);
  };
  // Explicit search (magnifier button / Enter) — fires immediately, no debounce.
  const doSearch = () => {
    const q = spotify.trim();
    if (q.length < 2) return;
    if (spotifyTrack) setSpotifyTrack(null);
    runSearch(q);
  };

  const variant = variantById(variantId);
  const protItem = cfg.protection.find((p) => p.id === protection) || cfg.protection[0];
  const spotifyOn = !!spotifyTrack;
  const total =
    (giftEdit ? 0 : variant.price) +
    extraLines * (cfg.linePrice || 0) +
    (protItem ? protItem.price : 0) +
    (cfg.isScrapbook ? photoTotal() : (spotifyOn ? (cfg.musicPrice || 0) : 0));

  const seeMore = (onClick) => html`<button type="button" class="pdp-seemore" onClick=${onClick}>${strings.seeMore} <svg class="pdp-seemore__i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg></button>`;

  const cartIcon = () => html`<svg class="pdp-btn__cart" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="8" cy="21" r="1"></circle><circle cx="19" cy="21" r="1"></circle><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path></svg>`;

  // Commemorative delivery deadline. The config island and the styles come from
  // snippets/delivery-deadline-banner.liquid; the selection runs here so the box
  // can sit under the price, and re-evaluates on every render (browser clock, so
  // the full-page cache can never pin an expired deadline).
  const ddbConfig = ddbModule.readConfig();
  const renderDeliveryBanner = () => {
    if (!ddbConfig) return '';
    const banner = ddbModule.selectBanner(ddbConfig, cfg.product.slug, Date.now() / 1000);
    if (!banner) return '';
    return html`
      <div class="ddb">
        <div class="ddb__header">
          <span class="ddb__icon" aria-hidden="true">
            <img src=${ddbGiftIconUrl} alt="" width="24" height="24" />
          </span>
          <p class="ddb__title">${banner.title}</p>
          <a class="ddb__cta" href="#datas-limite">${ddbConfig.ctaLabel}</a>
        </div>
        <p class="ddb__desc">${ddbModule.deadlineSentence(banner, ddbConfig.sentenceTemplate)}</p>
        ${banner.note && html`<p class="ddb__note">${banner.note}</p>`}
      </div>
    `;
  };

  // Variant-B-only UI (the personalization editor's single real
  // divergence today). Gated by abVariant at the call site — the shared
  // handlers/config/cart contract below are identical for both arms.
  const renderBanner = () => html`
    <div class="pdp-banner">
      <span class="pdp-banner__icon" aria-hidden="true">💡</span>
      <p class="pdp-banner__text">Com <strong>${strings.laterTitle}</strong>, ${strings.laterBanner}</p>
    </div>
  `;

  const renderProtection = () => html`
    <div class="pdp-field">
      <div class="pdp-addon-head">
        <p class="pdp-field__label">${strings.protectionTitle}</p>
        ${seeMore(openPopup('protecao'))}
      </div>
      <div class="pdp-prot">
        ${cfg.protection.map((p) => html`
          <button type="button" key=${p.id} class=${'pdp-prot__tile' + (p.id !== 'none' ? ' pdp-prot__tile--photo' : '') + (protection === p.id ? ' is-selected' : '')} onClick=${() => setProtection(p.id)}>
            ${p.id !== 'none' && (p.image
              ? html`<img class="pdp-prot__img" src=${p.image} alt=${p.label} loading="lazy" />`
              : html`<span class="pdp-prot__img pdp-prot__img--ph" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"></path></svg></span>`)}
            <span class="pdp-prot__name">${p.label}</span>
            ${p.price > 0 && html`<span class="pdp-prot__price">+${money(p.price)}</span>`}
            ${p.caption && html`<span class="pdp-prot__cap">${p.caption}</span>`}
          </button>
        `)}
      </div>
    </div>
  `;

  const renderSpotify = () => html`
    <div class="pdp-field">
      <div class="pdp-addon-head">
        <span class="pdp-addon-head__main">
          <span class="pdp-spotify-title"><svg class="pdp-spotify-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>${cfg.spotify.label} <span class="pdp-tag">${money(cfg.musicPrice || 0)}</span></span>
          ${strings.tipMusic && html`<${Tooltip} text=${strings.tipMusic} />`}
        </span>
        ${seeMore(openPopup('musica'))}
      </div>
      <p class="pdp-field__hint">${cfg.spotify.desc}</p>
      <p class="pdp-spotify-prompt">${strings.spotifySearchLabel}</p>
      <div class="pdp-spotify-search">
        <div class="pdp-spotify-row">
          <input class="pdp-input" placeholder=${strings.spotifySearchPlaceholder} value=${spotify} onInput=${(e) => onSpotifyInput(e.target.value)} onKeyDown=${(e) => { if (e.key === 'Enter') { e.preventDefault(); doSearch(); } }} />
          <button type="button" class="pdp-spotify-btn" aria-label=${strings.spotifySearchLabel} onClick=${doSearch}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="6.5"></circle><path d="m20 20-3.8-3.8"></path></svg>
          </button>
        </div>
        ${spotifyLoading && html`<p class="pdp-spotify-empty">${strings.spotifyLoading}</p>`}
        ${!spotifyLoading && spotifyError && html`<p class="pdp-spotify-empty">${strings.spotifyError}</p>`}
        ${!spotifyLoading && !spotifyError && spotifyResults.length > 0 && html`
          <ul class="pdp-spotify-list">
            ${spotifyResults.map((track) => html`
              <li class="pdp-spotify-item" key=${track.id}>
                <button type="button" class="pdp-spotify-result" onClick=${() => pickTrack(track)} aria-label=${strings.spotifyAdd + ': ' + track.name + ' — ' + track.artist}>
                  ${track.image
                    ? html`<img class="pdp-spotify-cover" src=${track.image} width="48" height="48" alt="" loading="lazy" />`
                    : html`<span class="pdp-spotify-cover pdp-spotify-cover--ph" aria-hidden="true"></span>`}
                  <span class="pdp-spotify-meta">
                    <span class="pdp-spotify-name">${track.name}</span>
                    <span class="pdp-spotify-artist">${track.artist}</span>
                  </span>
                  <span class="pdp-spotify-add" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"></path><path d="M5 12h14"></path></svg>
                  </span>
                </button>
              </li>
            `)}
          </ul>
        `}
        ${!spotifyLoading && !spotifyError && spotifySearched && spotifyResults.length === 0 && html`<p class="pdp-spotify-empty">${strings.spotifyEmpty}</p>`}
      </div>
      ${spotifyTrack && html`
        <p class="pdp-spotify-selected-label">${strings.spotifySelectedLabel}</p>
        <div class="pdp-spotify-selected">
          ${spotifyTrack.image
            ? html`<img class="pdp-spotify-cover" src=${spotifyTrack.image} width="48" height="48" alt=${spotifyTrack.name} loading="lazy" />`
            : html`<span class="pdp-spotify-cover pdp-spotify-cover--ph" aria-hidden="true"></span>`}
          <span class="pdp-spotify-meta">
            <span class="pdp-spotify-name">${spotifyTrack.name}</span>
            <span class="pdp-spotify-artist">${spotifyTrack.artist}</span>
          </span>
          <button type="button" class="pdp-spotify-remove" onClick=${clearTrack} aria-label=${strings.spotifyRemove}>${strings.spotifyRemoveShort}</button>
        </div>
      `}
    </div>
  `;

  // Scrapbook photo upsell — replaces Spotify. Banner + Autocolante/Tradicional
  // tabs + 5x6/9x12 size cards; selecting a size opens its quantity-discount picker.
  const renderPhotoAddon = () => {
    return html`
      <div class="pdp-field">
        <div class="pdp-photopromo">
          <span class="pdp-photopromo__ic" aria-hidden="true">📷</span>
          <span class="pdp-photopromo__body">
            <span class="pdp-photopromo__title">${PA.bannerTitle}</span>
            <span class="pdp-photopromo__sub">${PA.bannerSub}</span>
          </span>
        </div>
        <div class="pdp-phototabs" role="tablist">
          ${PA.tabs.map((tab) => html`
            <button type="button" key=${tab.id} role="tab" aria-selected=${photoTab === tab.id} class=${'pdp-phototab' + (photoTab === tab.id ? ' is-selected' : '')} onClick=${() => selectPhotoTab(tab.id)}>${tab.label}</button>
          `)}
        </div>
        <div class="pdp-photosizes">
          ${sizesForTab(photoTab).map((size) => {
            const quantity = photoQtys[size.id];
            const open = quantity != null;
            const per = size.prices[photoTab] || 0;
            const activeTier = open ? photoTierFor(quantity) : null;
            const pct = activeTier ? activeTier.p : 0;
            const sugg = (size.suggestion && size.suggestion[photoTab]) || 0;
            return html`
              <div class=${'pdp-photosize' + (open ? ' is-open' : '')} key=${size.id}>
                <div class="pdp-photosize__row">
                  <button type="button" class="pdp-photosize__toggle" onClick=${() => openSizeQty(size.id, open)}>
                    <span class=${'pdp-photosize__radio' + (open ? ' is-on' : '')} aria-hidden="true"></span>
                    <span class="pdp-photosize__info">
                      <span class="pdp-photosize__label">${size.label}</span>
                      ${sugg > 0 && html`<span class="pdp-photosize__sugg"><span class="pdp-photosize__sugg-full">${PA.suggestionLabel.replace('{n}', sugg)}</span><span class="pdp-photosize__sugg-short">${PA.suggestionShortLabel.replace('{n}', sugg)}</span></span>`}
                    </span>
                  </button>
                  ${open && html`
                    <button type="button" class="pdp-photosize__trash" aria-label=${strings.symbolsRemove} onClick=${() => setSizeQty(size.id, null)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path><path d="M10 11v6M14 11v6"></path></svg>
                    </button>
                  `}
                  ${(PA.thumb || (size.thumbs && size.thumbs[photoTab]))
                    ? html`<img class="pdp-photosize__thumb" src=${PA.thumb || size.thumbs[photoTab]} alt="" loading="lazy" />`
                    : html`<span class="pdp-photosize__thumb pdp-photosize__thumb--ph" aria-hidden="true"></span>`}
                </div>
                ${open && html`
                  <div class="pdp-photosize__divider"></div>
                  <div class="pdp-photopkg">
                    <div class="pdp-price__row">
                      <span class="pdp-photopkg__now">${money(Math.round(per * (1 - pct / 100)) * quantity)}</span>
                      <span class="pdp-price__was">${money(per * quantity)}</span>
                      ${activeTier && html`<span class="pdp-price__off">-${activeTier.p}%</span>`}
                    </div>
                    <p class="pdp-field__hint">${PA.helper}</p>
                    <div class="pdp-photopkg__head">
                      <span class="pdp-field__label">${PA.packagesLabel}</span>
                      ${sugg > 0 && PA.recommendedLabel && html`<span class="pdp-photopkg__featured">${PA.recommendedLabel.replace('{n}', sugg)}</span>`}
                    </div>
                    <div class="pdp-photopkg__pills">
                      ${PA.tiers.map((tier) => html`
                        <button type="button" key=${tier.q} class=${'pdp-pkgpill' + (quantity === tier.q ? ' is-selected' : '')} onClick=${() => { clearSizeDraft(size.id); setSizeQty(size.id, clampQty(tier.q, minFor(size))); }}>
                          <span class="pdp-pkgpill__q">${tier.q} un</span>
                          <span class="pdp-pkgpill__off">${tier.p}${PA.offSuffix}</span>
                        </button>
                      `)}
                    </div>
                    <div class="pdp-photoqty">
                      <div class="pdp-photoqty__lbl">
                        <span class="pdp-field__label">${PA.qtyLabel}</span>
                        <span class="pdp-field__hint">${PA.minLabel.replace('{n}', minFor(size))}</span>
                      </div>
                      <div class="pdp-stepper">
                        <button type="button" class="pdp-stepper__btn" onClick=${() => stepSizeQty(size.id, -1)} aria-label=${strings.a11yDecrease + ' — ' + size.label}>−</button>
                        <input
                          type="text"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          class="pdp-stepper__val"
                          autocomplete="off"
                          maxlength="4"
                          aria-label=${PA.qtyLabel + ' — ' + size.label}
                          value=${photoQtyDrafts[size.id] === undefined ? quantity : photoQtyDrafts[size.id]}
                          onInput=${(event) => onQtyInput(size.id, event)}
                          onBlur=${(event) => onQtyBlur(size.id, event)}
                          onKeyDown=${onQtyKeyDown}
                        />
                        <button type="button" class="pdp-stepper__btn" onClick=${() => stepSizeQty(size.id, 1)} aria-label=${strings.a11yIncrease + ' — ' + size.label}>+</button>
                      </div>
                    </div>
                  </div>
                `}
              </div>
            `;
          })}
          <p class="pdp-visually-hidden" role="status">${qtyStatus}</p>
        </div>
      </div>
    `;
  };

  // ── Engraving text fields ─────────────────────────────────
  // Passo 2 of the wizard and the drawer's flat edit panel render the SAME two
  // fields, so the limits, the per-line sanitising and the symbol pickers live
  // here at App scope rather than inside either view.
  const coverMax = coverLimit(titleSize);
  const sideMax = sideLimit(sideSize);
  // Re-trim each line to the new limit when the size changes (React parity).
  const onTitleSize = (size) => {
    setTitleSize(size);
    const limit = coverLimit(size);
    setCoverLines(coverLines.map((coverLine) => coverLine.slice(0, limit)));
  };
  const setCoverLine = (lineIndex, value) => {
    const clean = sanitizeText(value).slice(0, coverMax);
    setCoverLines(coverLines.map((coverLine, index) => (index === lineIndex ? clean : coverLine)));
  };
  const addCoverLine = () => {
    if (coverLines.length >= maxLines || coverLines.some((coverLine) => !coverLine.trim())) return;
    setCoverLines([...coverLines, '']);
    setCoverIcons([...coverIcons, '']);
  };
  const removeCoverLine = () => {
    if (coverLines.length > 1) {
      setCoverLines(coverLines.slice(0, -1));
      setCoverIcons(coverIcons.slice(0, -1));
    }
  };
  const pickIcon = (lineIndex, name) => {
    setCoverIcons(coverIcons.map((icon, index) => (index === lineIndex ? (icon === name ? '' : name) : icon)));
    setIconPicker(-1);
  };
  const pickSideIcon = (name) => {
    setSideIcon(sideIcon === name ? '' : name);
    setSidePicker(false);
  };
  const onSideSize = (size) => {
    setSideSize(size);
    const limit = sideLimit(size);
    setSideText(sideText.split('\n').map((sideLine) => sideLine.slice(0, limit)).join('\n'));
  };
  const emojiIcon = () => html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9.5"></circle><path d="M8.5 14.2s1.2 1.6 3.5 1.6 3.5-1.6 3.5-1.6"></path><path d="M9 9.6h.01"></path><path d="M15 9.6h.01"></path></svg>`;
  const plusIcon = () => html`<svg class="pdp-link__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9.5"></circle><path d="M8 12h8"></path><path d="M12 8v8"></path></svg>`;
  const minusIcon = () => html`<svg class="pdp-link__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9.5"></circle><path d="M8 12h8"></path></svg>`;

  const renderCoverTextField = () => html`
    <div class="pdp-field">
      <div class="pdp-field__labelrow">
        <label class="pdp-field__label">${strings.coverTextLabel}</label>
        ${strings.tipCoverText && html`<${Tooltip} text=${strings.tipCoverText} />`}
      </div>
      ${coverLines.map((line, lineIndex) => html`
        <div class="pdp-input-wrap" key=${'cl' + lineIndex}>
          <input class="pdp-input" maxlength=${coverMax} placeholder=${lineIndex === 0 ? strings.coverTextPlaceholder : ''}
            value=${line} onInput=${(event) => setCoverLine(lineIndex, event.target.value)} />
          <button type="button" class=${'pdp-input__emoji' + (coverIcons[lineIndex] ? ' is-active' : '')} aria-label=${strings.symbolsTitle} onClick=${() => setIconPicker(iconPicker === lineIndex ? -1 : lineIndex)}>${emojiIcon()}</button>
          ${iconPicker === lineIndex && html`
            <div class="pdp-iconpicker">
              <p class="pdp-iconpicker__title">${strings.symbolsTitle}</p>
              <div class="pdp-iconpicker__grid">
                ${(cfg.icons || []).map((ic) => html`
                  <button type="button" key=${ic.name} class=${'pdp-iconpicker__opt' + (coverIcons[lineIndex] === ic.name ? ' is-selected' : '')} aria-label=${ic.label || ic.name} onClick=${() => pickIcon(lineIndex, ic.name)}>
                    <img src=${ic.url} alt="" />
                  </button>
                `)}
              </div>
              <button type="button" class="pdp-iconpicker__remove" onClick=${() => pickIcon(lineIndex, '')}>${strings.symbolsRemove}</button>
            </div>
          `}
        </div>
      `)}
      <div class="pdp-line-actions">
        ${coverLines.length < maxLines && html`
          <button type="button" class="pdp-link pdp-link--add" onClick=${addCoverLine}>
            ${plusIcon()}<span>${strings.addLineLabel} <span class="pdp-link__muted">(+ ${money(cfg.linePrice || 0)})</span></span>
          </button>
        `}
        ${coverLines.length > 1 && html`
          <button type="button" class="pdp-link pdp-link--remove" onClick=${removeCoverLine}>
            ${minusIcon()}<span>${strings.removeLineLabel}</span>
          </button>
        `}
      </div>
      ${titleSizes.length > 1 && html`<${SegPills} label=${strings.titleSizeLabel} readout=${'Tamanho ' + titleSize} options=${titleSizes} value=${titleSize} onSelect=${onTitleSize} />`}
    </div>
  `;

  const renderSideTextField = () => (spineMode > 0 ? html`
    <div class="pdp-field">
      <label class="pdp-field__label">${strings.sideTextLabel}</label>
      <div class="pdp-input-wrap">
        <input class="pdp-input" maxlength=${sideMax} placeholder=${strings.sideTextPlaceholder}
          value=${sideText} onInput=${(event) => setSideText(sanitizeText(event.target.value).slice(0, sideMax))} />
        <button type="button" class=${'pdp-input__emoji' + (sideIcon ? ' is-active' : '')} aria-label=${strings.symbolsTitle} onClick=${() => setSidePicker(!sidePicker)}>${emojiIcon()}</button>
        ${sidePicker && html`
          <div class="pdp-iconpicker">
            <p class="pdp-iconpicker__title">${strings.symbolsTitle}</p>
            <div class="pdp-iconpicker__grid">
              ${(cfg.icons || []).map((ic) => html`
                <button type="button" key=${ic.name} class=${'pdp-iconpicker__opt' + (sideIcon === ic.name ? ' is-selected' : '')} aria-label=${ic.label || ic.name} onClick=${() => pickSideIcon(ic.name)}>
                  <img src=${ic.url} alt="" />
                </button>
              `)}
            </div>
            <button type="button" class="pdp-iconpicker__remove" onClick=${() => pickSideIcon('')}>${strings.symbolsRemove}</button>
          </div>
        `}
      </div>
      ${spineMode === 2 && html`<${SegPills} label=${strings.sideSizeLabel} readout=${'Tamanho ' + sideSize} options=${sideSizes} value=${sideSize} onSelect=${onSideSize} />`}
    </div>
  ` : null);

  const renderSummary = () => {
    const parts = [cfg.product.title];
    if (protItem && protItem.price > 0) parts.push(protItem.label);
    if (extraLines > 0) parts.push(strings.lineAddonLabel);
    if (cfg.isScrapbook && PA) {
      PA.sizes.forEach((s) => {
        const q = photoQtys[s.id];
        if (q != null) parts.push(q + ' fotos ' + s.id + 'cm');
      });
    } else if (spotifyOn) {
      parts.push(cfg.spotify.label);
    }
    const summaryName = parts.join(' + ');
    return html`
      <div class="pdp-recap">
        <div class="pdp-recap__left">
          <span class="pdp-recap__name">${summaryName}</span>
          ${variant.compareAt && variant.compareAt > variant.price && html`<span class="pdp-recap__was">${money(variant.compareAt)}</span>`}
        </div>
        <span class="pdp-recap__total">${money(total)}</span>
      </div>
    `;
  };

  // Build the line-item properties that capture the full personalization.
  // Customer-visible keys render in the cart drawer / checkout / order; the
  // hidden `_personalizacao` key (leading underscore => the drawer omits it)
  // stores the exact structured state for production + future cart editing.
  const buildProperties = () => {
    const props = {};
    if (color && color.label) props['Cor da capa'] = color.label;
    // Hidden (leading _): the exact colour preview image the buyer saw, resolved
    // the same way the live Preview does (colorImages by selected colour, then the
    // product's previewBase). Lets the cart drawer show that colour instead of the
    // variant's default featured image. Mirrors the `_spotify_image` pattern.
    const coverImages = cfg.colorImages || [];
    const coverIndex = color ? cfg.colors.findIndex((coverColor) => coverColor.label === color.label) : 0;
    const coverImage = coverImages[coverIndex] || coverImages[0] || (cfg.product && cfg.product.previewBase) || '';
    if (color && coverImage) props['_cover_image'] = coverImage;
    // Hidden hex of the selected colour, so the cart drawer paints the option
    // swatch with the actual colour instead of the default grey placeholder.
    if (color && color.hex) props['_cover_hex'] = color.hex;
    // Hidden: the S3 public URL of the captured personalization preview —
    // attached verbatim; the order ingest drops any other URL shape.
    const previewUrl = previewCapture.current();
    if (previewUrl) props['_preview_image'] = previewUrl;
    if (foil && foil.label) props['Cor da gravação'] = foil.label;
    const lines = coverLines.map((line) => line.trim()).filter(Boolean);
    if (lines.length) {
      props['Texto da capa'] = lines.join(' / ');
      props['Tamanho do título'] = titleSize;
    }
    const symbols = coverIcons.map((name) => (name ? iconLabelMap[name] : '')).filter(Boolean);
    if (symbols.length) props['Símbolos da capa'] = symbols.join(' / ');
    const side = sideText.trim();
    if (side) {
      props['Texto da lateral'] = side;
      if (spineMode === 2) props['Tamanho da lateral'] = sideSize;
    }
    if (sideIcon) props['Símbolo da lateral'] = iconLabelMap[sideIcon];
    props['_personalizacao'] = JSON.stringify({
      cor: color ? { label: color.label, hex: color.hex } : null,
      gravacao: foil ? foil.id : null,
      titulo: {
        tamanho: titleSize,
        linhas: coverLines.map((coverLine, index) => ({ texto: coverLine, simbolo: coverIcons[index] || null })),
      },
      lateral: { texto: sideText, tamanho: spineMode === 2 ? sideSize : null, simbolo: sideIcon || null },
    });
    return props;
  };

  // The album line + its add-ons as a /cart/add.js items[] array, all sharing
  // `group`. Used by both addToCart (fresh group) and saveEdit (fresh group +
  // old group removed). Scrapbook photo packs keep their own independent groups.
  const buildCartItems = (group, kind, { commit = true } = {}) => {
    const albumProps = (kind === 'full') ? buildProperties() : {};
    albumProps._group = group;
    const items = [{ id: variantId, quantity: 1, properties: albumProps }];
    if (protItem && protItem.id !== 'none' && protItem.variantId) {
      items.push({ id: protItem.variantId, quantity: 1, properties: applyOrigin({ _group: group, _addon: protItem.id === 'luva' ? 'caixa_luva' : 'caixa_cristal' }, editAddonOrigins.current) });
    }
    if (hasMusic && spotifyTrack && cfg.musicVariantId) {
      // 'Música' is customer-visible (cart/checkout/order); the _-prefixed props
      // are hidden but kept for the drawer cover thumbnail + admin track link.
      const songLabel = trackLabel(spotifyTrack);
      items.push({ id: cfg.musicVariantId, quantity: 1, properties: applyOrigin({ _group: group, _addon: 'musica', 'Música': songLabel, _spotify_image: spotifyTrack.image || '', _spotify_url: spotifyTrack.url || '' }, editAddonOrigins.current) });
    }
    if (extraLines > 0 && cfg.lineVariantId) {
      items.push({ id: cfg.lineVariantId, quantity: extraLines, properties: { _group: group, _addon: 'linha' } });
    }
    // Scrapbook photo upsell: each chosen size is an INDEPENDENT Fotos product line
    // (own fresh _group, no _addon), not nested under the album, so the album edit
    // never touches it. photoTab picks auto/trad; size picks 5x6/9x12.
    if (cfg.isScrapbook && PA && PA.sizes) {
      PA.sizes.forEach((size) => {
        // The draft, when one exists, is what the shopper is looking at — WebKit
        // doesn't blur a focused field on button tap, so an emptied field never
        // reaches photoQtys (onQtyInput only commits digits > 0) and this would
        // otherwise resend whatever quantity was committed before it was cleared.
        const draft = photoQtyDrafts[size.id];
        const raw = draft === undefined ? photoQtys[size.id] : parseInt(draft, 10);
        const vid = variantIdFor(size, photoTab);
        if (raw != null && vid) {
          // Re-clamp at the cart boundary: WebKit doesn't blur a focused input on
          // button tap, so a package/stepper click can commit past the size's
          // minimum or the operator's ceiling while the field never re-validates;
          // clampQty also absorbs the NaN a cleared draft parses to.
          const qty = clampQty(raw, minFor(size));
          // The correction has to reach the screen too. photoTotal() and the size
          // row price both read photoQtys, so leaving the raw value there would
          // charge for `qty` while the page still shows the price for `raw`. The
          // draft goes with it either way — a resolved draft must not keep
          // rendering over the committed number.
          if (commit && (qty !== raw || draft !== undefined)) {
            setSizeQty(size.id, qty);
            clearSizeDraft(size.id);
          }
          const photoGroup = newGroupId();
          items.push({ id: vid, quantity: qty, properties: { _group: photoGroup, 'Tamanho': size.id + 'cm', 'Quantidade': String(qty) } });
        }
      });
    }
    return items;
  };

  // Real add-to-cart: POST the album variant (+ properties on the full path),
  // request the re-rendered cart-drawer section, then hand it to the drawer
  // via its `cart:open` hook (which also refreshes the header cart badge).
  const addToCart = async (kind) => {
    if (adding || !variant.available) return;
    setAddError('');
    setAdding(true);
    // Catch an upload the recap started moments ago; the timeout keeps a hung
    // presign from blocking the purchase — the line then ships without the
    // preview property and the drawer falls back.
    if (kind === 'full') await previewCapture.settle(4000);
    const group = newGroupId();
    const items = buildCartItems(group, kind);
    try {
      const res = await fetch(cfg.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: 'cart-drawer', sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAddError((data && (data.description || data.message)) || strings.addError);
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: data.sections } }));
      // Its own event, because `cart:open` is fired by the drawer's edit flow
      // too: listening to that one would count an edited line as a PDP add.
      document.dispatchEvent(new CustomEvent('fn:pdp-added'));
    } catch {
      setAddError(strings.addError);
    } finally {
      setAdding(false);
    }
  };

  // Add the replacement FIRST, then remove the old lines: a failed add then
  // leaves the buyer's group untouched, which remove-first cannot promise.
  // Remove-first looks safer and is not — the line key hashes the properties and
  // every save mints a fresh `_group`, so the two never collide.
  //
  // The old keys are re-read from /cart.js at save time so a cart change since
  // load can't leave us zeroing the wrong (or a gone) line.
  const saveGiftEdit = async () => {
    if (editSaving.current || adding) return;
    editSaving.current = true;
    setAddError('');
    setAdding(true);
    await previewCapture.settle(4000);
    try {
      const [album, ...rest] = buildCartItems(giftEdit.group, 'full');
      const sections = await saveAlbumGift({
        routes: cfg.routes,
        group: giftEdit.group,
        chosen: album.properties,
        addons: rest.filter((item) => item.properties._group === giftEdit.group && GIFT_ADDONS.includes(item.properties._addon)),
        messages: strings,
        sectionsUrl: window.location.pathname,
      });
      if (panel && panelRoot) panelRoot.dispatchEvent(new CustomEvent('fn:edit-saved', { detail: { sections } }));
      else document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections } }));
    } catch (error) {
      if (error && error.sections) document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: error.sections } }));
      setAddError((error && error.message) || strings.addError);
    } finally {
      editSaving.current = false;
      setAdding(false);
    }
  };

  const saveEdit = async () => {
    if (giftEdit) return saveGiftEdit();
    // `adding` stays in the condition because it also covers the add-to-cart
    // path, which shares the flag.
    if (editSaving.current || adding || !variant.available) return;
    editSaving.current = true;
    setAddError('');
    setAdding(true);
    await previewCapture.settle(4000);
    try {
      let sections = null;
      const attempt = editSaveAttempt.current;
      // The idempotency key identifies the PAYLOAD, not the attempt. The Cart
      // AJAX API offers none, so the group uuid stands in — but only while the
      // buyer has not changed anything: the screen stays editable behind a failed
      // save, and reusing the id after an edit would skip the add and leave the
      // bag holding a version they never confirmed.
      const signature = JSON.stringify(buildCartItems('', 'full'));
      const sameAsAttempt = !!attempt && attempt.signature === signature;
      const group = sameAsAttempt ? attempt.groupId : newGroupId();
      let oldKeys = attempt ? attempt.oldKeys : (editGroupKeys.current || []);
      let oldGroups = attempt ? (attempt.oldGroups || []) : (editGroupId.current ? [editGroupId.current] : []);
      let replacementIn = !!(sameAsAttempt && attempt.replacementIn);
      // Whether THIS call is the one that added. Only an add invalidates the
      // line keys read above, so only it has to resolve them again before
      // removing.
      let addedNow = false;

      if (!replacementIn) {
        // A read that fails leaves the load-time keys standing: a Shopify line key
        // is derived from the variant plus its properties, so a stale one either
        // names the same logical line or matches nothing. A read that SUCCEEDS and
        // does not find the line is different — the album was removed elsewhere
        // while this screen was open, and carrying on would re-add a line the
        // buyer deleted.
        let cart = null;
        try {
          const cartRes = await fetch(cfg.routes.cart, { headers: { Accept: 'application/json' } });
          if (cartRes.ok) cart = await cartRes.json();
        } catch { /* keep the load-time keys */ }
        // A retry that cannot see the bag cannot tell whether its own previous
        // add landed. Adding blind is exactly how two replacements happen.
        if (!cart && attempt) {
          setAddError(strings.addError);
          return;
        }
        if (cart) {
          const cartItems = cart.items || [];
          const keysOfGroup = (id) => cartItems.filter((cartItem) => (cartItem.properties || {})._group === id).map((cartItem) => cartItem.key);
          // A previous attempt that no longer matches what the buyer wants left a
          // group behind nobody asked for. It goes out with the old one.
          const staleKeys = attempt && !sameAsAttempt ? keysOfGroup(attempt.groupId) : [];
          const staleGroups = attempt && !sameAsAttempt && attempt.groupId ? [attempt.groupId] : [];

          if (sameAsAttempt && keysOfGroup(group).length) {
            replacementIn = true;
          } else {
            const current = cartItems.find((cartItem) => cartItem.key === editKey);
            if (isGiftLine(current)) {
              setAddError(strings.addError);
              return;
            }
            if (current) {
              const currentGroup = (current.properties || {})._group;
              // Both, because they answer different questions: the keys are what
              // a save that adds nothing zeroes right away, and the group is
              // what re-finds these lines once an add has re-keyed the bag.
              const currentKeys = currentGroup ? keysOfGroup(currentGroup) : [current.key];
              oldKeys = currentKeys.concat(staleKeys.filter((key) => !currentKeys.includes(key)));
              oldGroups = (currentGroup ? [currentGroup] : []).concat(staleGroups.filter((id) => id !== currentGroup));
            } else if (staleKeys.length) {
              // The line this screen opened on is gone, but the abandoned
              // attempt's group is in the bag — that group is what this save now
              // replaces, rather than being left behind forever.
              oldKeys = staleKeys;
              oldGroups = staleGroups;
            } else {
              setAddError(strings.lineGone || strings.addError);
              return;
            }
          }
        }
      }

      if (!replacementIn) {
        const items = buildCartItems(group, 'full');
        // Recorded BEFORE the request, not after: `fetch` and `.json()` can both
        // reject once the POST has already reached Shopify, and an attempt that
        // is not written down loses its group id — which is exactly how the next
        // click adds a second copy.
        editSaveAttempt.current = { groupId: group, oldKeys, oldGroups, signature, replacementIn: false };
        const addRes = await fetch(cfg.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: 'cart-drawer', sections_url: window.location.pathname }),
        });
        const addData = await addRes.json();
        if (!addRes.ok) {
          // Nothing was removed, so the bag still holds exactly what the buyer
          // had — which is the whole reason the add goes first.
          setAddError((addData && (addData.description || addData.message)) || strings.addError);
          return;
        }
        sections = addData.sections || sections;
        editSaveAttempt.current = { groupId: group, oldKeys, oldGroups, signature, replacementIn: true };
        addedNow = true;
      }
      // One atomic update, not a change.js per line — the same call the drawer's
      // own "remove this group" button makes (removeGroup in cart-drawer.liquid).
      // A loop can fail halfway and orphan the add-ons around an album that is
      // already gone.
      const updates = {};
      if ((addedNow || replacementIn) && oldGroups.length) {
        // Resolve the keys NOW, because the add this call just made is what
        // invalidates the ones read above. Zeroing a stale key removes the
        // REPLACEMENT instead of the original, and the damage is quiet: the
        // buyer keeps the old personalization, while the new add-ons stay behind
        // in a group no album carries — the drawer only draws an add-on inside
        // the album whose `_group` matches, so it vanishes from the bag and is
        // still charged at checkout. `replacementIn` counts too: a retry that
        // adds nothing because a previous attempt already did is holding keys
        // THAT add invalidated. Only a save with no replacement in the bag at
        // all can trust the keys it read. Same read, same reason as
        // assets/pdp-fotos.js.
        let bag = null;
        try {
          const bagRes = await fetch(cfg.routes.cart, { headers: { Accept: 'application/json' } });
          if (bagRes.ok) bag = await bagRes.json();
        } catch { /* answered by the guard below */ }
        // Both versions are in the bag and nothing here can tell which keys to
        // zero. Better a duplicate the buyer can see and delete than a guess
        // that removes the wrong one.
        // A body without an items array is as useless as no body at all: it
        // would resolve to zero keys, skip the removal and end the save
        // reporting success with the old copy still in the bag.
        if (!bag || !Array.isArray(bag.items)) {
          if (sections) document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections } }));
          setAddError(strings.oldCopyLeft || strings.addError);
          return;
        }
        (bag.items || []).forEach((item) => {
          const itemGroup = (item.properties || {})._group;
          if (itemGroup && itemGroup !== group && oldGroups.indexOf(itemGroup) > -1) updates[item.key] = 0;
        });
      } else {
        oldKeys.forEach((oldKey) => { updates[oldKey] = 0; });
      }
      // The old group is already gone — removed elsewhere, or by a retry that got
      // further than it managed to record. An empty update is a no-op.
      if (Object.keys(updates).length) {
        const upRes = await fetch(cfg.routes.cartUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: 'cart-drawer', sections_url: window.location.pathname }),
        });
        if (!upRes.ok) {
          // Both versions are in the bag. Re-render it so the copy the message asks
          // them to delete is actually on screen; the retry finishes only this step.
          if (sections) document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections } }));
          setAddError(strings.oldCopyLeft || strings.addError);
          return;
        }
        const upData = await upRes.json();
        sections = upData.sections || sections;
      }
      // On the PDP the save is also the navigation back to the cart, so it opens
      // the drawer. Inside the drawer's edit panel the drawer is already open and
      // the host owns what happens next (slide back, toast, re-render), so the
      // result is reported to it instead — see assets/cart-edit-host.js.
      if (panel && panelRoot) {
        panelRoot.dispatchEvent(new CustomEvent('fn:edit-saved', { detail: { sections } }));
      } else {
        document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections } }));
      }
    } catch {
      setAddError(strings.addError);
    } finally {
      // Released here, not on the next render: the screen stays editable behind a
      // failed save, and a retry must be able to start immediately.
      editSaving.current = false;
      setAdding(false);
    }
  };

  // ── Bridge to the drawer's edit panel ─────────────────────
  // The panel's SAVE button is the drawer's own footer pill, outside this tree
  // (it has to sit below the scroll area, which the tree does not own). So the
  // host asks for a save through an event, and gets the button's state back the
  // same way. Contract documented in assets/cart-edit-host.js.
  const panelBusy = adding;
  const panelCanSave = !!editKey && !adding && (!!variant.available || !!giftEdit);
  useEffect(() => {
    if (!panel || !panelRoot) return undefined;
    const onSave = () => { saveEdit(); };
    panelRoot.addEventListener('fn:edit-save', onSave);
    return () => panelRoot.removeEventListener('fn:edit-save', onSave);
  }, [panel, panelRoot, saveEdit]);
  useEffect(() => {
    if (!panel || !panelRoot) return;
    panelRoot.dispatchEvent(new CustomEvent('fn:edit-state', {
      detail: { busy: panelBusy, canSave: panelCanSave, error: addError },
    }));
  }, [panel, panelRoot, panelBusy, panelCanSave, addError]);

  // ── Edit panel (cart drawer) ──────────────────────────────
  // One scrolling column, no steps: the buyer already chose everything once, so
  // this screen is a form to correct, not a wizard to walk. Same components and
  // the same order the recap reads back — cover colour, photo count, foil,
  // engraving, sizes, add-ons.
  if (panel) {
    return html`
      <div class="pdp-config pdp-config--panel">
        <${SwatchGrid} items=${cfg.colors} selected=${color} onSelect=${setColor} label=${strings.colorLabel} readout=${true} checkSelect=${true} tip=${strings.tipColor} />
        ${!cfg.isScrapbook && hasQtyAxis && !giftEdit && html`<span class="pdp-rule"></span><${QtyPills} selectedId=${variantId} onSelect=${setVariantId} />`}
        <span class="pdp-rule"></span>
        <${SwatchGrid} items=${cfg.foils} selected=${foil} onSelect=${setFoil} label=${strings.foilLabel} inlineReadout=${true} />
        <span class="pdp-rule"></span>
        ${renderCoverTextField()}
        ${spineMode > 0 && html`<span class="pdp-rule"></span>${renderSideTextField()}`}
        <span class="pdp-rule"></span>
        ${renderProtection()}
        ${!cfg.isScrapbook && hasMusic && html`<span class="pdp-rule"></span>${renderSpotify()}`}
        <span class="pdp-rule"></span>
        ${renderSummary()}
        ${/* The id the drawer's SAVE button points `aria-describedby` at, so a
             failed save is announced with the button rather than only in the
             scroll area above it. */ ''}
        ${addError && html`<p id="cart-edit-error" class="pdp-add-error" role="alert">${addError}</p>`}
      </div>
    `;
  }

  // ── Main view ─────────────────────────────────────────────
  if (mode === 'main') {
    const ratingIsLink = !!strings.reviewsUrl;
    const RatingTag = ratingIsLink ? 'a' : 'div';
    return html`
      <div class="pdp-config">
        <nav class="pdp-breadcrumb" aria-label="breadcrumb">
          <a class="pdp-breadcrumb__link" href=${cfg.product.productsUrl}>${strings.breadcrumbRoot}</a>
          <span class="pdp-breadcrumb__sep" aria-hidden="true">›</span>
          <span class="pdp-breadcrumb__current">${cfg.product.title}</span>
        </nav>

        <div class="pdp-heading">
          <div class="pdp-titlerow">
            <h1 class="pdp-config__title">${cfg.product.title}</h1>
            ${cfg.product.sizeLabel && html`<span class="pdp-size-badge">${cfg.product.sizeLabel.replace(/^Álbum\s*/i, '')}</span>`}
          </div>
          <${RatingTag}
            class=${ratingIsLink ? 'pdp-rating pdp-rating--link' : 'pdp-rating'}
            href=${ratingIsLink ? strings.reviewsUrl : null}
            target=${ratingIsLink ? '_blank' : null}
            rel=${ratingIsLink ? 'noopener noreferrer' : null}
          >
            <span class="pdp-rating__stars" aria-hidden="true">
              ${[0, 1, 2, 3].map((starIndex) => html`<svg key=${starIndex} class="pdp-star" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path></svg>`)}
              <span class="pdp-star-half"><svg class="pdp-star-half__bg" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path></svg><svg class="pdp-star-half__fg" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path></svg></span>
            </span>
            <span class="pdp-rating__text">${strings.ratingValue} · <strong>${strings.ratingCount}</strong></span>
          <//>
        </div>

        <${PriceBlock} variant=${variant} />
        ${!panel && html`<${PromoGiftBadge} />`}
        ${renderDeliveryBanner()}
        <span class="pdp-rule"></span>
        ${!cfg.isScrapbook && html`<${QtyPills} selectedId=${variantId} onSelect=${setVariantId} /><span class="pdp-rule"></span>`}

        <p class="pdp-section-label">${strings.personalizeQuestion}</p>
        <div class="pdp-choice">
          <button type="button" class=${'pdp-choice__card' + (choice === 'depois' ? ' pdp-choice__card--accent' : '')} onClick=${() => setChoice('depois')}>
            <span class=${'pdp-choice__chip' + (choice === 'depois' ? ' pdp-choice__chip--dark' : '')}>
              <svg class="pdp-choice__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>
            </span>
            <span class="pdp-choice__body">
              <span class="pdp-choice__titlerow">
                <span class="pdp-choice__title">${strings.laterTitle}</span>
                <span class="pdp-choice__badge pdp-choice__badge--blue">${strings.laterBadge}</span>
              </span>
              <span class="pdp-choice__desc">${strings.laterDesc}</span>
            </span>
            <span class=${'pdp-choice__radio' + (choice === 'depois' ? ' pdp-choice__radio--on' : '')} aria-hidden="true">
              ${choice === 'depois' && html`<svg class="pdp-choice__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"></path></svg>`}
            </span>
          </button>
          <button type="button" class=${'pdp-choice__card' + (choice === 'agora' ? ' pdp-choice__card--accent' : '')} onClick=${() => setChoice('agora')}>
            <span class=${'pdp-choice__chip' + (choice === 'agora' ? ' pdp-choice__chip--dark' : '')}>
              <svg class="pdp-choice__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path></svg>
            </span>
            <span class="pdp-choice__body">
              <span class="pdp-choice__titlerow">
                <span class="pdp-choice__title">${strings.nowTitle}</span>
                <span class="pdp-choice__badge pdp-choice__badge--amber">${strings.nowBadge}</span>
              </span>
              <span class="pdp-choice__desc">${strings.nowDesc}</span>
            </span>
            <span class=${'pdp-choice__radio' + (choice === 'agora' ? ' pdp-choice__radio--on' : '')} aria-hidden="true">
              ${choice === 'agora' && html`<svg class="pdp-choice__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"></path></svg>`}
            </span>
          </button>
        </div>

        ${choice === 'depois'
          ? html`
              ${abVariant === 'B' ? html`${renderBanner()}<span class="pdp-rule"></span>` : ''}
              ${renderProtection()}
              ${cfg.isScrapbook
                ? (hasPhotoAddon ? html`<span class="pdp-rule"></span>${renderPhotoAddon()}` : null)
                : (hasMusic ? html`<span class="pdp-rule"></span>${renderSpotify()}` : null)}
              <span class="pdp-rule"></span>
              ${renderSummary()}
              <button type="button" class="pdp-btn pdp-btn--fill pdp-btn--hero" disabled=${adding || !variant.available} onClick=${() => addToCart('later')}>
                <span>${adding ? strings.adding : strings.addToCart}</span>
                ${cartIcon()}
              </button>
              ${addError && html`<p class="pdp-add-error" role="alert">${addError}</p>`}
            `
          : html`
              ${renderSummary()}
              <button type="button" class="pdp-btn pdp-btn--fill pdp-btn--hero" onClick=${() => setMode(1)}>
                <span>${strings.personalizeNow}</span>
                <svg class="pdp-btn__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"></path></svg>
              </button>
            `}
      </div>
    `;
  }

  // ── Wizard ────────────────────────────────────────────────
  const Nav = ({ next, nextLabel }) => html`
    <div class="pdp-step__nav">
      <button type="button" class="pdp-btn pdp-btn--fill pdp-btn--continue" onClick=${next}>
        <span>${nextLabel || strings.continue}</span>
        <svg class="pdp-btn__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"></path></svg>
      </button>
      ${!(editKey && mode === 1) && html`
        <button type="button" class="pdp-btn pdp-btn--stroke pdp-btn--back" onClick=${() => setMode(mode === 1 ? 'main' : mode - 1)}>${strings.back}</button>
      `}
    </div>
  `;

  if (mode === 1) {
    return html`
      <div class="pdp-config pdp-config--wizard">
        <${StepHeader} step=${1} title=${strings.colorStepTitle} />
        <${SwatchGrid} items=${cfg.colors} selected=${color} onSelect=${setColor} label=${strings.colorLabel} readout=${true} checkSelect=${true} tip=${strings.tipColor} />
        ${editKey && !cfg.isScrapbook && !giftEdit && html`<span class="pdp-rule"></span><${QtyPills} selectedId=${variantId} onSelect=${setVariantId} />`}
        ${editKey && html`<span class="pdp-rule"></span>${renderSummary()}`}
        <${Nav} next=${() => setMode(2)} />
      </div>
    `;
  }

  if (mode === 2) {
    return html`
      <div class="pdp-config pdp-config--wizard">
        <${StepHeader} step=${2} title=${strings.textStepTitle} />

        <${SwatchGrid} items=${cfg.foils} selected=${foil} onSelect=${setFoil} label=${strings.foilLabel} inlineReadout=${true} />
        <span class="pdp-rule"></span>

        ${renderCoverTextField()}
        <span class="pdp-rule"></span>

        ${renderSideTextField()}

        <${Nav} next=${() => setMode(3)} />
      </div>
    `;
  }

  // mode === 3
  return html`
    <div class="pdp-config pdp-config--wizard">
      <${StepHeader} step=${3} title=${strings.addonsStepTitle} />
      ${renderProtection()}
      ${cfg.isScrapbook
        ? (editKey || !hasPhotoAddon ? null : html`<span class="pdp-rule"></span>${renderPhotoAddon()}`)
        : (hasMusic ? html`<span class="pdp-rule"></span>${renderSpotify()}` : null)}
      ${renderSummary()}
      ${addError && html`<p class="pdp-add-error" role="alert">${addError}</p>`}
      <div class="pdp-step__nav">
        <button type="button" class="pdp-btn pdp-btn--fill" disabled=${adding || (!variant.available && !giftEdit)} onClick=${() => (editKey ? saveEdit() : addToCart('full'))}><span>${adding ? strings.adding : (editKey ? strings.saveChanges : strings.addToCart)}</span>${cartIcon()}</button>
        <button type="button" class="pdp-btn pdp-btn--stroke pdp-btn--back" onClick=${() => setMode(2)}>${strings.back}</button>
      </div>
    </div>
  `;
}

// ── Live preview (gallery) ────────────────────────────────
// Recolours the book by swapping to the selected colour's photo and
// overlays cover/side text with a gold/silver foil fill. Ported from
// the React ProductMask (Classic Pocket positioning, container 600/841).
function Preview() {
  const st = useStore();
  const active = st.mode !== 'main';
  const images = cfg.colorImages || [];
  const targetSrc = images[st.colorIndex] || images[0] || cfg.product.previewBase || '';
  const [displaySrc, setDisplaySrc] = useState(targetSrc);

  // Warm the whole colour set the first time the wizard opens, so later swaps are instant.
  useEffect(() => {
    if (!active || Preview._warmed) return;
    Preview._warmed = true;
    images.forEach((u) => {
      if (u) {
        const im = new Image();
        im.src = u;
      }
    });
  }, [active]);

  // Keep the current photo on screen until the next colour's photo has decoded (no blank flash).
  useEffect(() => {
    if (!targetSrc || targetSrc === displaySrc) return undefined;
    let cancelled = false;
    const im = new Image();
    const swap = () => {
      if (!cancelled) setDisplaySrc(targetSrc);
    };
    im.src = targetSrc;
    if (im.decode) {
      im.decode().then(swap, swap);
    } else {
      im.onload = swap;
      im.onerror = swap;
    }
    return () => {
      cancelled = true;
    };
  }, [targetSrc]);

  if (!active) return null; // show the Liquid photo gallery instead
  const foilClass = st.foilId === 'prata' ? 'pdp-preview--silver' : 'pdp-preview--gold';
  const coverStyle = `--cx:${previewGeo.cx}%;--cy:${previewGeo.cy}%;--fs:${coverFontFor(st.titleSize).toFixed(2)}cqw`;
  const sideStyle = `--sx:${previewGeo.sx}%;--sy:${previewGeo.sy}%;--fs:${spineFontFor(st.sideSize).toFixed(2)}cqw`;
  const cIcons = st.coverIcons || [];
  const coverLines = (st.coverText || '').split('\n')
    .map((t, i) => ({ text: t, icon: cIcons[i] || '' }))
    .filter((l) => l.text.trim().length || l.icon);
  const side = (st.sideText || '').trim();
  const sideSym = st.sideIcon || '';
  return html`
    <div class="pdp-preview-stage">
      <div class=${'pdp-preview ' + foilClass}>
        ${displaySrc && html`<img class="pdp-preview__img" src=${displaySrc} alt="" draggable="false" />`}
        ${coverLines.length > 0 && html`
          <div class="pdp-preview__cover" style=${coverStyle}>
            ${coverLines.map((l, i) => html`<p class="pdp-preview__line" key=${i}>${l.text}${l.icon && iconMap[l.icon] ? previewSymbol(l.icon) : ''}</p>`)}
          </div>
        `}
        ${(side || sideSym) && html`
          <div class="pdp-preview__side" style=${sideStyle}>
            <p class="pdp-preview__line">${side}${sideSym && iconMap[sideSym] ? previewSymbol(sideSym) : ''}</p>
          </div>
        `}
      </div>
    </div>
  `;
}

// ── Mounting ──────────────────────────────────────────────
// One tree at a time. The PDP's and the drawer panel's would otherwise share
// `cfg` and the capture store while showing two different products, so opening
// the panel on a PDP tears the PDP's tree down and closing it puts it back.
let activeMount = null;
// Monotonic token. `mount()` awaits the runtime, and a second open during that
// await calls configure() again — so a mount that resumes afterwards would arm
// the capture and render the OTHER product's config into its own root. The token
// is claimed before the await and re-checked after: a superseded mount stops.
let mountToken = 0;

/**
 * Render the configurator into `root`, against whatever configure() last bound.
 *
 * @param {object}      options
 * @param {Element}     options.root         where the buy box / edit form renders
 * @param {Element}     [options.previewRoot] gallery overlay host (PDP only)
 * @param {string}      [options.editKey]     cart line key to hydrate from
 * @param {boolean}     [options.panel]       render the drawer's flat edit screen
 *
 * Rejects when a newer mount (or an unmount) supersedes this one. It has to
 * REJECT rather than resolve quietly: a caller that only awaits would otherwise
 * carry on and hand out a teardown handle for a tree it never rendered, and
 * calling that handle would unmount whatever is on screen instead.
 */
export async function mount({ root, previewRoot, editKey, panel }) {
  if (!cfg) throw new Error('pdp-configurator: configure() before mount()');
  if (activeMount) unmount();
  // Claim a token before yielding. `cfg` and its twelve derived values move
  // together in configure(), so a mount that resumes after a concurrent
  // configure() cannot put them back — the only safe thing it can do is not
  // render. The token, not a captured copy of `cfg`, is what enforces that:
  // holding a copy would let a loser render with a config whose derived state
  // no longer matches it.
  const token = ++mountToken;
  await loadRuntime(cfg.assets);
  if (token !== mountToken) throw new Error('pdp-configurator: mount superseded');
  armCapture();
  activeMount = { root, previewRoot: previewRoot || null };
  root.removeAttribute('data-loading');
  render(html`<${App} panel=${!!panel} panelRoot=${panel ? root : null} initialEditKey=${editKey || null} />`, root);
  if (previewRoot) render(html`<${Preview} />`, previewRoot);
}

/**
 * Tear the current tree down and stop its capture subscription.
 *
 * Bumping the token also cancels a mount still waiting on the runtime — going
 * back before the panel finished loading must not leave a tree behind.
 */
export function unmount() {
  mountToken += 1;
  if (!activeMount) return;
  render(null, activeMount.root);
  if (activeMount.previewRoot) render(null, activeMount.previewRoot);
  activeMount = null;
  if (detachCapture) {
    detachCapture();
    detachCapture = null;
  }
  previewCapture = INERT_CAPTURE;
}

// ── PDP auto-mount ────────────────────────────────────────
// Unchanged behaviour: a document carrying the island IS the PDP, and importing
// this module mounts the buy box. `pdpBootstrap` remembers what to restore after
// the drawer's edit panel has borrowed the module for another product.
const pdpIsland = pageDocument && pageDocument.getElementById('pdp-config');
const pdpRoot = pageDocument && pageDocument.getElementById('pdp-configurator-root');
let pdpBootstrap = null;
if (pdpIsland && pdpRoot) {
  pdpBootstrap = {
    config: JSON.parse(pdpIsland.textContent),
    root: pdpRoot,
    previewRoot: pageDocument.getElementById('pdp-preview-layer'),
  };
  configure(pdpBootstrap.config);
  await mount(pdpBootstrap);
}

// ── Cart-drawer edit surface ──────────────────────────────
// Claim the `album` surface so the drawer edits an album in place instead of
// navigating to `?edit=`. Registration is order-tolerant: the host lazy-imports
// this module on click (so it is always up first on a non-PDP page), but the
// PDP's own eager <script> can run before the host's, and module execution order
// between two <script type="module"> tags is not something to rely on.
function registerAlbumSurface(cartEdit) {
  cartEdit.register('album', async ({ root, editKey, productUrl }) => {
    const productDocument = await cartEdit.loadProductDocument(productUrl);
    const island = productDocument.getElementById('pdp-config');
    if (!island) throw new Error('pdp-configurator: product page carries no #pdp-config');
    configure(JSON.parse(island.textContent));
    await mount({ root, editKey, panel: true });
    return {
      destroy() {
        unmount();
        // Put the PDP back when the panel was opened over one. It returns to its
        // initial state, which is what navigating to `?edit=` already did.
        if (pdpBootstrap) {
          configure(pdpBootstrap.config);
          mount(pdpBootstrap);
        }
      },
    };
  });
}

if (pageWindow && pageWindow.fnCartEdit) {
  registerAlbumSurface(pageWindow.fnCartEdit);
} else if (pageDocument) {
  pageDocument.addEventListener('fn:cart-edit-ready', () => registerAlbumSurface(pageWindow.fnCartEdit), { once: true });
}
